
import { useCallback, useEffect, useRef, useState } from 'react';
import paper, { Tool, Path, Point, Group } from 'paper';
import ImageUploading from 'react-images-uploading';

import { useTranslation } from "react-i18next";
import { userAPI } from "../UserAPI";
import { useAdministrationAPI } from "../AdministrationAPI";
import { getConvexPolygonArea, Space } from "@the-real-insight/tri-model";
import { useDrop } from "react-dnd";
import { getAssetIcon } from "../components/Icon";
import { useNavigate } from "react-router-dom";

import { Eraser, Loader2, Maximize2, Redo2, Ruler, Trash2, Undo2, ZoomIn, ZoomOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from '@/lib/utils';
import {
    canRedo,
    canUndo,
    EMPTY_HISTORY,
    EditHistory,
    EditStep,
    redoLabel,
    redoStep,
    remember,
    undoLabel,
    undoStep,
} from './editHistory';
import { zoomFactorFor } from './zoomGesture';
import { loadIconFontForCanvas } from '../utilities/iconFont';
import { MARKER_CUE_FALLBACK, MARKER_CUE_GLYPHS, markerPaint, type MarkerStyle } from '../utilities/assetStatus';
import {
    angleAt,
    canRemoveVertex,
    insertVertexAfter,
    moveVertex,
    nearestEdge,
    nudgeFor,
    rightAngleMarker,
    snapToRightAngle,
    removeVertex,
    toPointList,
} from './polygonEditing';
interface Properties {
    floor: any;
    floorChanged?: (floor: any) => void
    spaces?: any[],
    spacesChanged?: () => void,
    assets?: any[];
    assetsChanged?: () => void,
    selectedSpaceIndex?: any;
    /** A room on the plan was clicked — its index in `spaces`. */
    spaceClicked?: (index: number) => void;
    /**
     * Der ausgewählte Raum soll gelöscht werden — gefragt über das Papierkorbzeichen am Raum.
     *
     * Der Editor löscht nicht selbst: Die Liste der Räume gehört dem Aufrufer, und die Rückfrage
     * vor dem Löschen soll dieselbe sein wie in der Raumtabelle. Ohne diesen Rückruf erscheint das
     * Zeichen gar nicht.
     */
    spaceDeleteRequested?: (space: any) => void;
    selectedSpaceIndexChanged?: (space: any) => void;
    /**
     * Vor dem Ersetzen des Plans gefragt: liefert `false`, wenn der Benutzer abbricht (#735).
     * Räume und Gerätepositionen gelten für den alten Plan — wer sie behält, behält Zahlen,
     * die auf dem neuen Bild irgendwo landen. Der Eigentümer der Etage entscheidet, was damit
     * geschieht; der Editor lädt erst hoch, wenn er zugestimmt hat.
     */
    beforeImageReplaced?: () => Promise<boolean>;
    /**
     * Gerät, das auf den Plan gesetzt werden soll: der nächste Tipp auf den Plan platziert es
     * (#735, iPad). Ziehen ist auf einem Touchgerät keine Option — react-dnd läuft über
     * HTML5-Drag-Events, und die feuern dort nicht.
     */
    placementAsset?: any;
    /** Platzierung ist durch oder wurde abgebrochen — der Aufrufer räumt seinen Zustand ab. */
    placementFinished?: () => void;
    /** Die Position ist gespeichert — erst hier darf eine Rückmeldung erscheinen. */
    placementSaved?: (asset: any) => void;
    /**
     * Anlage auf dem Plan angetippt (nicht gezogen) — mit der Tipp-Position in
     * Fensterkoordinaten, damit der Aufrufer sein Menü dort öffnen kann (#735).
     */
    assetClicked?: (asset: any, screenPoint: { x: number; y: number }) => void;
    /**
     * Status look per asset `_id` (tri-server#1364): ring colour, a cue glyph in a dot on the
     * marker's corner ("!", clock, wrench …), a dashed ring for old data, faint when outside the
     * active filter. Assets not in the map keep their own look (DIN 276 category colour).
     * Changing it restyles the drawn markers in place — the plan is not rebuilt.
     */
    assetMarkerStyles?: Record<string, MarkerStyle>;
    /**
     * Rooms drawn faint and grey (devices tab): the status colours of the markers must be the
     * loudest thing on the plan. The rooms tab leaves it off.
     */
    muteRooms?: boolean;
    readonly?: boolean;
}

/** Unterhalb dieser Bildschirmbewegung ist es ein Tipp, keine Verschiebung. */
const DRAG_THRESHOLD_PX = 5;

/** Womit ausgestochen wird: eckig oder rund. */
type StencilShape = 'square' | 'circle';

/**
 * Ein Ausstich: eine weiße Fläche im Grundrissbild.
 *
 * Abgelegt in **Bildpunkten** und nicht in Projektkoordinaten: Das Bild ist das, was gespeichert
 * wird, und seine Punkte bleiben dieselben, wie auch immer gerade gezoomt und geschoben ist.
 */
interface StencilCut {
    /** Linke obere Ecke des umschließenden Quadrats, in Bildpunkten. */
    x: number;
    y: number;
    /** Kantenlänge — beim Kreis sein Durchmesser. In Bildpunkten. */
    size: number;
    shape: StencilShape;
}

/**
 * Die vier Größen des Stempels, in **Metern am Gebäude**.
 *
 * Nicht in Bildpunkten: Ein Grundriss kann mit 30 Punkten je Meter eingescannt sein oder mit 210,
 * und dieselbe Fläche in Punkten wäre dort ein Schrank und hier ein Saal.
 *
 * Die kleinste ist ein Zehntelmeter — klein genug für eine Bemaßungszahl, einen Pfeilkopf, eine
 * einzelne Tür in der Wand. Von dort aufwärts jeweils gut das Dreifache, bis zu drei Metern für
 * ein ganzes Schriftfeld.
 */
const STENCIL_SIZES_METRES = [0.1, 0.3, 1, 3];

/**
 * Wie klein ein Stempel im Bild werden darf, in Bildpunkten.
 *
 * Auf einem grob eingescannten Plan wären zehn Zentimeter drei Punkte — nicht mehr zu treffen und
 * nicht mehr zu sehen. Unterhalb dieser Schwelle gilt sie statt des Maßes.
 */
const STENCIL_MINIMUM_PX = 6;

/**
 * Die Fläche eines Stempels als paper-Pfad, um einen Mittelpunkt herum.
 *
 * An einer Stelle, weil dieselbe Form dreimal gebraucht wird: für die Ausstiche über dem Bild, für
 * das Quadrat oder den Kreis, der dem Zeiger vorausläuft, und — sinngemäß — beim Zusammensetzen des
 * Bildes zum Speichern. Ginge das auseinander, zeigte die Vorschau etwas anderes, als gespeichert
 * wird.
 */
const stencilPath = (shape: StencilShape, centre: any, size: number): any =>
    (shape === 'circle'
        ? new Path.Circle(centre, size / 2)
        : new Path.Rectangle(
            new paper.Rectangle(
                new Point(centre.x - size / 2, centre.y - size / 2),
                new paper.Size(size, size),
            ),
        ));

const scaleFormat = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Für die Stempelgrößen: „0,5 m" und „3 m", nicht „0,50 m" und „3,00 m". */
const stencilSizeFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 });

// Asset marker icons are a small set of static SVGs, but a floor can carry thousands of assets
// (a large building has hundreds of rooms plus every window/door). Fetching `svgs/<id>.svg` once
// per asset — as `paper.importSVG(url)` does — fires thousands of concurrent XHRs and crashes the
// browser with ERR_INSUFFICIENT_RESOURCES. Fetch each distinct icon's markup exactly once, module-
// wide, then import a template and clone it per asset (no re-parse, no per-asset network).
const svgTextCache = new Map<string, string>();
const svgFetchInFlight = new Map<string, Promise<void>>();

const preloadIconSvg = (id: string): Promise<void> => {
    if (svgTextCache.has(id)) {
        return Promise.resolve();
    }
    let inFlight = svgFetchInFlight.get(id);
    if (!inFlight) {
        inFlight = (async () => {
            try {
                const response = await fetch(`svgs/${id}.svg`);
                if (response.ok) {
                    svgTextCache.set(id, await response.text());
                }
            } catch {
                // Missing/failed icon: markers still render (box + label), just without the glyph.
            } finally {
                svgFetchInFlight.delete(id);
            }
        })();
        svgFetchInFlight.set(id, inFlight);
    }
    return inFlight;
};

export function FloorplanEditor(properties: Properties) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const canvasRef = useRef() as any;
    const distanceInputRef = useRef() as any;
    const [images, setImages] = useState([]);
    const { updateFloor, updateFloorplanImage, updateAsset, updateSpace } = useAdministrationAPI();
    // useAdministrationAPI returns a fresh updateSpace on every render. Keeping
    // it in the canvas-draw effect's deps would tear down and rebuild paper.js
    // (paper.project.remove()) on every render — the floorplan flashes then
    // vanishes. Access it through a ref instead so it's never a dependency.
    const updateSpaceRef = useRef(updateSpace);
    updateSpaceRef.current = updateSpace;
    const panZoomRef = useRef({
        imageOffset: new Point(0, 0),
        // Pan is stored as an offset from the floorplan's centre, not as an absolute
        // view centre: the view centre moves when the canvas is resized, so an
        // absolute value made the plan jump on every resize.
        panOffset: new Point(0, 0),
        zoom: -1, // Uninitialized
        lastPoint: null as any,
        dragAssetItem: null as any,
        // Unterscheidet Tippen von Ziehen: nur ein Tipp öffnet das Menü.
        assetDragged: false,
        // Fensterkoordinaten des Tipps, damit das Menü dort aufgeht.
        pointerScreen: { x: 0, y: 0 },
        // Welche Ecke gerade gezogen wird; -1 heißt keine.
        dragVertexIndex: -1,
        // Unterscheidet Tippen von Ziehen, wie `assetDragged` bei den Anlagen.
        vertexDragged: false,
        // Wurde in diesem Zug der Ausschnitt verschoben? Ein Schwenk ist kein Klick.
        panned: false,
    });
    // Centre of the floorplan raster in project coordinates, needed to translate the
    // stored pan offset back into a view centre after a rebuild.
    const rasterCenterRef = useRef<any>(null);
    // Maße des tatsächlich geladenen Bildes. Die Etage führt zwar `floorplanImageWidth/Height`,
    // aber nicht jeder Upload-Weg schreibt sie — fehlen sie, rechnete die Einpassung mit Zoom 1
    // weiter und der Plan stand angeschnitten im Ausschnitt. Der Raster weiß es immer.
    const rasterSizeRef = useRef<{ width: number; height: number } | null>(null);
    /** Begrenzt die Selbstanstöße, wenn paper einen fertigen Raster als "nicht geladen" meldet. */
    const forcedRedrawsRef = useRef(0);
    const pinchRef = useRef({ active: false, distance: 0 });
    // Zeichnen ohne Tastatur: die Leiste braucht Zugriff auf dieselben zwei Aktionen, die
    // ENTER und ESC auslösen. Sie leben im Zeichen-Effekt, also hier der Griff darauf.
    const drawingActionsRef = useRef<null | { finish: () => void; cancel: () => void }>(null);
    const [drawnPointCount, setDrawnPointCount] = useState(0);
    // Aktuelles Platzierungsziel für den paper-Tool-Handler, der außerhalb des Renders lebt.
    const placementRef = useRef<any>(null);
    // False while the plan is still showing the automatic fit. As long as that holds,
    // a new canvas size refits the plan; once someone has zoomed or panned, their view
    // is kept and only the "fit to window" button brings the whole plan back.
    const userAdjustedViewRef = useRef(false);
    // Set while drawing: paper.js turns the FIRST finger of a pinch into a mouse
    // click, which drops a stray polygon point on the plan. The drawing branch
    // parks a function here that takes that just-placed point back.
    const pinchDrawingGuardRef = useRef<(() => void) | null>(null);
    // The editor fills the column it is given instead of a fixed 800x600 box. The
    // canvas' pixel size follows the measured container, and the fit zoom is derived
    // from that measurement rather than from a hardcoded 800.
    const [canvasNode, setCanvasNode] = useState<any>(null);
    const [canvasContainer, setCanvasContainer] = useState<any>(null);
    const [canvasSize, setCanvasSize] = useState({ width: 800, height: 600 });
    // Höhe/Breite des geladenen Plans. Die Box nimmt dieses Verhältnis an, damit neben dem
    // Plan keine toten Streifen stehen bleiben: eingepasst wird seitenverhältnistreu, und was
    // die Box mehr hat als der Plan, bleibt sonst leer.
    const [planAspect, setPlanAspect] = useState<number | null>(null);
    // Asset marker groups (icon + label + position pin) drawn in project space. They are counter-
    // scaled on zoom so they keep a constant on-screen size while the floorplan and spaces zoom.
    const markerGroupsRef = useRef<any[]>([]);
    // Current per-asset status styles; read while drawing and by `restyleMarkers`.
    const markerStylesRef = useRef<Record<string, MarkerStyle> | undefined>(properties.assetMarkerStyles);
    const muteRoomsRef = useRef<boolean>(!!properties.muteRooms);

    /**
     * One marker in its current look: with a status a strong ring in the status colour plus the
     * cue dot (shape, not colour alone), dashed for old data, faint outside the filter; without
     * one its own category colour and no cue.
     */
    const styleMarker = (group: any) => {
        const tint = group?.data?.tint;
        if (!group || !tint) {
            return;
        }
        const style = markerStylesRef.current?.[String(group.data.asset?._id)];
        try {
            const paint = markerPaint(style, tint.baseColor);
            tint.box.strokeColor = new paper.Color(paint.boxColor);
            tint.box.strokeWidth = paint.strokeWidth;
            tint.box.dashArray = paint.dashArray;
            tint.label.fillColor = new paper.Color(paint.labelColor);
            if (tint.glyph) {
                tint.glyph.fillColor = new paper.Color(paint.glyphColor);
            }
            if (tint.cueDot && tint.cueText) {
                tint.cueDot.visible = paint.showCue;
                tint.cueText.visible = paint.showCue;
                if (paint.cue) {
                    tint.cueDot.fillColor = new paper.Color(paint.boxColor);
                    tint.cueText.content = tint.fontReady
                        ? MARKER_CUE_GLYPHS[paint.cue]
                        : MARKER_CUE_FALLBACK[paint.cue];
                }
            }
            group.opacity = paint.opacity;
        } catch {
            // group may have been removed mid-redraw; ignore.
        }
    };

    /** Restyle every drawn marker (status or filter changed). */
    const restyleMarkers = () => {
        if (!paper.project) {
            return;
        }
        for (const group of markerGroupsRef.current) {
            styleMarker(group);
        }
        paper.view?.update?.();
    };

    /**
     * Die Eckgriffe gegen den Zoom skalieren, damit sie gleich groß bleiben.
     *
     * Ihr Radius wird beim Aufbau einmal aus `1/zoom` gerechnet — danach ist er eine Länge im
     * Projekt und wüchse beim Hineinzoomen mit. Ein Griff ist aber ein Bedienelement: Er soll unter
     * dem Zeiger immer gleich groß sein, gleich wie nah der Plan herangeholt ist.
     *
     * Jeder Kreis wird um seine eigene Mitte skaliert; die Ecke, auf der er sitzt, bleibt also
     * stehen. Das Winkelzeichen fällt weg statt mitzuwachsen — es entsteht beim nächsten
     * Ziehschritt ohnehin neu, und ein Rest davon in falscher Größe wäre schlimmer als keiner.
     */
    const rescaleVertexHandles = (oldZoom: number, newZoom: number) => {
        const editing = vertexEditRef.current;

        if (!editing || !paper.project || !paper.view) {
            return;
        }

        if (!oldZoom || !newZoom || oldZoom === newZoom) {
            return;
        }

        const factor = oldZoom / newZoom;

        for (const handle of editing.handles ?? []) {
            if (!handle?.parent) {
                continue;
            }

            try {
                handle.scale(factor, handle.position);
            } catch {
                // Der Griff kann mitten in der Bewegung entfernt worden sein.
            }
        }

        editing.dropSnapMarker?.();
    };

    /** Rescale every marker around its pin anchor so it stays the same screen size across a zoom step. */
    const rescaleMarkers = (oldZoom: number, newZoom: number) => {
        // Ohne aktives Projekt hat paper keinen Kontext: `setPosition` misst den Beschriftungstext
        // und wirft dann beim Zugriff auf den fehlenden Kontext.
        if (!paper.project || !paper.view) {
            return;
        }

        if (!oldZoom || !newZoom || oldZoom === newZoom) {
            return;
        }
        const factor = oldZoom / newZoom;
        for (const group of markerGroupsRef.current) {
            const anchor = group?.data?.anchor;
            if (!group || !group.parent || !anchor) {
                continue;
            }
            try {
                group.scale(factor, new Point(anchor.x, anchor.y));
            } catch {
                // group may have been removed mid-interaction; ignore.
            }
        }
    };
    const [floor, setFloor] = useState([]) as any;
    const [floorplanImageLoaded, setFloorplaImageLoaded] = useState(false);
    /** Das Planbild kam nicht an — dann sagt die Fläche das, statt leer zu bleiben. */
    const [floorplanImageFailed, setFloorplanImageFailed] = useState(false);
    const [spaces, setSpaces] = useState([]) as any;

    const [selectedSpaceIndex, setSelectedSpaceIndex] = useState([]) as any;

    /**
     * Der Raum, dessen Ecken bearbeitet werden — oder -1.
     *
     * Ein Zustand und nicht `path.selected` von paper: Die Griffe müssen neu gezeichnet werden,
     * wenn sich die Auswahl ändert, und an einer Eigenschaft des Pfades hängt kein Neuzeichnen.
     */
    const [vertexEditIndex, setVertexEditIndex] = useState(-1);

    /** Derselbe Wert für Abschlüsse, die den Aufbau der Szene überleben. */
    const vertexEditIndexRef = useRef(-1);

    vertexEditIndexRef.current = vertexEditIndex;

    /**
     * Welcher Raum die Eckenauswahl trägt — an seiner Kennung, nicht an seinem Platz in der Liste.
     *
     * Ein Platz ist keine Zusage: Wird ein Raum gelöscht, rücken die dahinter auf, und ein Index
     * meinte danach einen anderen Raum. Die Kennung meint weiter denselben — oder keinen mehr.
     */
    const vertexEditSpaceIdRef = useRef<string | null>(null);

    /**
     * Die Arbeitskopie während des Ziehens, in Projektkoordinaten.
     *
     * Das Ziehen darf nicht bei jedem Mausschritt speichern und neu zeichnen — es bewegt die
     * paper-Objekte unmittelbar und legt erst beim Loslassen die neue Form ab.
     */
    const vertexEditRef = useRef<any>(null);

    /**
     * Laufende Nummer für Speichervorgänge — damit spätere Antworten nicht von früheren
     * überschrieben werden.
     */
    const persistSequenceRef = useRef(0);

    /**
     * Die gewählte Ecke des bearbeiteten Raums, oder -1.
     *
     * Ein Ref und kein Zustand: Die Auswahl ändert nur, wie ein Kreis aussieht, und das zeichnet
     * paper unmittelbar. Als Zustand baute jede Auswahl die Griffe neu auf — und mit ihnen die
     * Arbeitskopie, was eine noch nicht abgelegte Verschiebung verwürfe.
     */
    const selectedVertexRef = useRef(-1);

    /**
     * Zu welchem Raum die gewählte Ecke gehört.
     *
     * Das Ablegen ändert `spaces`, davon baut der Effekt die Griffe neu — und ohne dieses Merkmal
     * wäre die Auswahl danach fort. Wer eine Kante um einen halben Meter rückt, drückt fünfmal,
     * wartet auf das Speichern und stünde dann ohne Ecke da.
     */
    const vertexSelectionRoomRef = useRef(-1);

    /**
     * Die gezeichneten Raumpfade und die Funktion, die sie erneuert.
     *
     * Über sie läuft das Neuzeichnen nach einer Formänderung, ohne dass die Szene — und mit ihr das
     * Rasterbild — neu aufgebaut wird.
     */
    const roomPathsRef = useRef<any[]>([]);
    const drawRoomsRef = useRef<((current: any[]) => void) | null>(null);

    /**
     * Die Räume, wie sie gerade sind — für Abschlüsse, die den Aufbau überleben.
     *
     * Der Aufbau der Szene hängt bewusst nicht mehr an `spaces`; wer dort die aktuelle Liste
     * braucht, nimmt sie von hier statt aus einem Abschluss, der beim nächsten Speichern veraltet
     * wäre.
     */
    const spacesRef = useRef<any[]>([]);

    spacesRef.current = spaces;

    /** Die einzige Stelle, an der die Eckenauswahl gesetzt wird: Index und Kennung gehören zusammen. */
    const selectRoomVertices = useCallback((index: number) => {
        vertexEditSpaceIdRef.current = index >= 0 ? (spacesRef.current?.[index]?._id ?? null) : null;

        setVertexEditIndex(index);
        // Beides sind Refs und damit stabil.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Die Auswahl der Liste nachführen, wenn sich unter ihr etwas ändert.
     *
     * Wird ein Raum gelöscht — aus der Tabelle oder über den Papierkorb im Plan —, rücken die
     * dahinter auf. Ohne diesen Abgleich zeigte der Rahmen danach auf den Nachbarn, und der
     * Papierkorb daran hätte den Falschen erwischt.
     */
    useEffect(() => {
        if (vertexEditIndex < 0) {
            return;
        }

        const id = vertexEditSpaceIdRef.current;

        if (!id) {
            return;
        }

        const at = (spaces ?? []).findIndex((space: any) => space?._id === id);

        if (at !== vertexEditIndex) {
            selectRoomVertices(at);
        }
    }, [spaces, vertexEditIndex, selectRoomVertices]);

    /**
     * Was die Werkzeugleiste über die Eckenbearbeitung sagt.
     *
     * Bewusst neben dem Ref und nicht statt seiner: Der Ref treibt das Zeichnen, dieser Zustand nur
     * den Text. Stünde er in den Abhängigkeiten des Effekts, baute jede Auswahl die Griffe neu auf.
     */
    const [vertexUi, setVertexUi] = useState({ selected: false, snapped: false, angle: null as number | null });

    /**
     * Wie weit der Winkel von 90° abweichen darf und trotzdem gefangen wird.
     *
     * Sieben Grad sind der Bereich, in dem eine Wand „eigentlich rechtwinklig gemeint" ist —
     * genug, um beim Ziehen spürbar zu halten, zu wenig, um einen erkennbar schiefen Winkel
     * geradezuziehen.
     */
    const SNAP_TOLERANCE_DEGREES = 7;

    /** Bogenradius des Winkelzeichens, in Bildschirmpixeln. */
    const RIGHT_ANGLE_MARKER_PX = 16;

    /** Die Farbe des Eingerastetseins — abgesetzt vom Auswahlblau, damit sie etwas anderes sagt. */
    const SNAP_GREEN = '#12a150';

    /**
     * Wo das Papierkorbzeichen des ausgewählten Raums steht, in Pixeln der Zeichenfläche — oder
     * `null`, solange keiner ausgewählt ist.
     *
     * Es liegt als gewöhnliche Schaltfläche über dem Bild und nicht als Zeichnung darin: So trägt
     * es seinen Kurztext, ist mit der Tastatur erreichbar und bleibt bei jedem Zoom gleich groß.
     */
    const [deleteAnchor, setDeleteAnchor] = useState<{ x: number; y: number } | null>(null);

    /**
     * Das Ausstechen: ob der Stempel geführt wird, wie groß er ist, und was er getroffen hat.
     *
     * Die Ausstiche stehen als Liste und werden **nicht** ins Bild gerechnet, das hier angezeigt
     * wird: Gezeichnet sind sie weiße Rechtecke über dem Rasterbild. Damit ist Rückgängig eine
     * Bewegung in dieser Liste und kostet keinen Speicher für zwanzig vollständige Bilder — und
     * das, was hochgeladen wird, entsteht jedesmal frisch aus dem ursprünglichen Bild und der
     * Liste. Genau deshalb wirkt ein Rückgängig auch auf das, was gespeichert ist.
     */
    const [stencilActive, setStencilActive] = useState(false);
    const [stencilMetres, setStencilMetres] = useState(STENCIL_SIZES_METRES[1]);
    const [stencilShape, setStencilShape] = useState<StencilShape>('square');
    const [stencilMenuOpen, setStencilMenuOpen] = useState(false);
    const [cuts, setCuts] = useState<StencilCut[]>([]);
    const [stencilSaving, setStencilSaving] = useState(false);

    /** Größe des Stempels in Bildpunkten — der Raster steht unskaliert, also 1:1. */
    const stencilEdge = Math.max(
        STENCIL_MINIMUM_PX,
        Math.round(stencilMetres * (floor?.floorplanImageScale || 0)),
    );

    /**
     * Was die Werkzeuge im Aufbau der Szene über den Stempel wissen müssen.
     *
     * `active` wird weiter unten gesetzt, sobald feststeht, ob nicht gerade gezeichnet oder
     * skaliert wird — dort hängt ein anderes Werkzeug am Klick.
     */
    const stencilRef = useRef({ active: false, edge: 0, shape: 'square' as StencilShape });

    stencilRef.current.edge = stencilEdge;
    stencilRef.current.shape = stencilShape;

    /** Das Rasterbild — die Ausstiche werden unmittelbar darüber eingehängt, unter allem anderen. */
    const rasterRef = useRef<any>(null);

    /**
     * Der Verlauf — für alles im Editor, nicht nur für das Ausstechen.
     *
     * Ein Schritt bringt selbst mit, wie er sich zurücknimmt; hier steht nur die Reihenfolge. So
     * legen ein Ausstich, eine verschobene Ecke und eine neu gezeichnete Raumform denselben Schritt
     * ab und werden in derselben Reihenfolge zurückgenommen, in der sie geschahen — auch über die
     * Werkzeuge hinweg.
     */
    const [history, setHistory] = useState<EditHistory>(EMPTY_HISTORY);
    const historyRef = useRef<EditHistory>(EMPTY_HISTORY);

    historyRef.current = history;

    /** Einen Schritt aufnehmen. Stabil, weil Werkzeuge im Aufbau der Szene ihn festhalten. */
    const rememberEdit = useCallback((step: EditStep) => {
        setHistory((current) => remember(current, step));
    }, []);

    const runUndo = useCallback(() => {
        const { history: next, step } = undoStep(historyRef.current);

        if (!step) {
            return;
        }

        // Erst ausführen, dann den Verlauf fortschreiben: Der Schritt kann selbst Zustand setzen,
        // und ein halb bewegter Verlauf wäre schwerer zu lesen als ein noch nicht bewegter.
        step.undo();
        setHistory(next);
    }, []);

    const runRedo = useCallback(() => {
        const { history: next, step } = redoStep(historyRef.current);

        if (!step) {
            return;
        }

        step.redo();
        setHistory(next);
    }, []);

    /** ⌘Z und ⌘⇧Z — wonach jeder greift, der etwas rückgängig machen will. */
    useEffect(() => {
        if (properties.readonly) {
            return;
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') {
                return;
            }

            const target = event.target as HTMLElement | null;

            // In einem Eingabefeld gehört ⌘Z dem Feld.
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                return;
            }

            event.preventDefault();

            if (event.shiftKey) {
                runRedo();
            } else {
                runUndo();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [properties.readonly, runUndo, runRedo]);

    /**
     * Eine Raumform ablegen und zeigen — der Weg, den ein Rückgängig an einer Form nimmt.
     *
     * Derselbe wie beim Bearbeiten selbst: Ein zurückgenommener Zug muss ebenso gespeichert werden
     * wie der Zug. Ein Rückgängig, das nur auf dem Bildschirm gilt, wäre eine Zusage, die beim
     * nächsten Laden gebrochen wird.
     *
     * Als Ref, weil die Schritte im Verlauf ihn über Renderzyklen hinweg festhalten.
     */
    const applySpaceRef = useRef<(space: any) => void>(() => {});

    applySpaceRef.current = (next: any) => {
        /*
         * Dieselbe laufende Nummer wie beim Bearbeiten — sonst gewönne ein alter Zug gegen das
         * Zurücknehmen.
         *
         * Beim Ziehen einer Ecke ist eine Speicherung oft noch unterwegs, wenn schon Rückgängig
         * gedrückt wird. Ohne die Nummer trüge jene Antwort ihre Form später in den Zustand ein:
         * Auf dem Schirm stünde wieder der bearbeitete Umriss, während der Verlauf längst weiter
         * ist. Das Hochzählen entwertet die ältere Antwort und schützt zugleich diese hier.
         */
        const thisSequence = ++persistSequenceRef.current;

        void updateSpaceRef.current(next).then(() => {
            if (thisSequence !== persistSequenceRef.current) {
                return;
            }

            const list = (spacesRef.current ?? []).map((space: any) =>
                (space._id === next._id ? next : space));

            setSpaces(list);

            if (properties.spacesChanged) {
                properties.spacesChanged();
            }

            if (properties.floorChanged) {
                properties.floorChanged({ ...properties.floor, spaces: list });
            }
        });
    };

    /** Das Quadrat, das dem Zeiger vorausläuft, damit man sieht, was getroffen wird. */
    const stencilPreviewRef = useRef<any>(null);

    /** Kurzlebiger Hinweis über der Zeichenfläche — etwa, dass die letzten drei Ecken bleiben. */
    const [vertexHint, setVertexHint] = useState('');

    useEffect(() => {
        if (!vertexHint) {
            return;
        }

        const timer = setTimeout(() => setVertexHint(''), 2500);

        return () => clearTimeout(timer);
    }, [vertexHint]);
    const [assets, setAssets] = useState([]) as any;
    const [scaling, setScaling] = useState(false) as any;
    const [scalingDialogOpen, setScalingDialogOpen] = useState(false) as any;
    const [scalingMeasurement, setScalingMeasurement] = useState(1) as any;
    const [scalingDistance, setScalingDistance] = useState(1) as any;

    /**
     * Ob wirklich ausgestochen wird.
     *
     * Zeichnen und Skalieren bauen ein **eigenes** Werkzeug auf; dort steckt der Ausstich gar nicht
     * im Klick. Stünde der Stempel trotzdem auf dem Plan, sähe ein Klick wie ein Ausstich aus und
     * setzte in Wahrheit einen Polygonpunkt oder eine Messstrecke. Der Zustand allein genügt also
     * nicht — es zählt, ob nicht gerade ein anderes Werkzeug am Klick hängt.
     */
    const stencilRunning = stencilActive && !properties.readonly && selectedSpaceIndex < 0 && !scaling;

    stencilRef.current.active = stencilRunning;

    /**
     * Ein anderes Werkzeug übernimmt: Der Stempel wird weggelegt.
     *
     * Ihn nur wirkungslos zu stellen, ließe sein Zeichen hervorgehoben stehen — man hielte ihn für
     * bereit. Wer zeichnen oder skalieren will, hat sich für etwas anderes entschieden.
     */
    useEffect(() => {
        if (stencilActive && (selectedSpaceIndex >= 0 || scaling)) {
            setStencilActive(false);
            setStencilMenuOpen(false);
        }
    }, [stencilActive, selectedSpaceIndex, scaling]);
    // Bumped when paper's Raster finishes loading asynchronously, to re-run the
    // draw effect once the image is actually ready (large images can construct
    // the raster before it reports `loaded`).
    const [redrawTick, setRedrawTick] = useState(0);

    let distancePath: any = null;
    let distanceText: any = null;

    placementRef.current = properties.placementAsset ?? null;

    const toBuildingCoordinates = (point: any, imageScale: number, imageXOffset: number, imageYOffset: number) => {
        return { x: (point.x - imageXOffset) / imageScale * 1000, y: (point.y - imageYOffset) / imageScale * 1000 };
    };

    const toProjectCoordinates = (point: any, imageScale: number, imageXOffset: number, imageYOffset: number) => {
        // Exact inverse of toBuildingCoordinates: both x and y ADD their offset.
        // The y term previously subtracted imageYOffset, which is not the inverse
        // and pushed assets/room shapes ~2*imageYOffset off (off-screen).
        return { x: point.x / 1000 * imageScale + imageXOffset, y: point.y / 1000 * imageScale + imageYOffset };
    };

    const [{ isOver: _isOver }, drop] = useDrop(() => ({
        accept: 'AssetDnD',
        drop: async (item: any, monitor: any) => {
            if (distancePath) {
                distancePath.remove();
                distanceText.remove();
            }

            console.log('Client offset >>>', monitor.getClientOffset());

            const dropPosition = monitor.getClientOffset();
            const canvasBoundingBox = canvasRef.current.getBoundingClientRect();

            dropPosition.x -= canvasBoundingBox.x;
            dropPosition.y -= canvasBoundingBox.y;

            console.log('Drop position on canvas >>>', monitor.getClientOffset());

            const assetLocation = toBuildingCoordinates(paper.view.viewToProject(dropPosition), floor.floorplanImageScale, panZoomRef.current.imageOffset.x, panZoomRef.current.imageOffset.y)

            const asset = assets.find((asset: any) => asset._id === item._id);

            // (Re)position asset

            if (asset) {
                const index = assets.indexOf(asset);
                const newAssets = [...assets];
                const newAsset = { ...asset, ...assetLocation, floor: properties.floor._id };

                newAssets[index] = newAsset;

                setAssets(newAssets);

                await updateAsset(newAsset)
            }
        },
        hover: (_item: any, _monitor: any) => {
        },
        collect: (monitor: any) => ({
            isOver: !!monitor.isOver(),
        }),
    }), [assets])

    const onChange = async (imageList: any, addUpdateIndex: any) => {
        try {
            console.log(imageList, addUpdateIndex);

            if (imageList && imageList.length) {
                const img = new Image();

                img.src = imageList[0].data_url;

                img.onload = async () => {
                    console.log('Height >>>', img.height);
                    console.log('Width', img.width);

                    // Erst fragen, dann hochladen — danach ist der alte Plan weg.
                    if (properties.beforeImageReplaced) {
                        const proceed = await properties.beforeImageReplaced();

                        if (!proceed) {
                            setImages([]);

                            return;
                        }
                    }

                    const formData = new FormData();

                    formData.append("floorplanImage", imageList[0].file);
                    formData.append("width", `${img.width}`);
                    formData.append("height", `${img.height}`);

                    await updateFloorplanImage(properties.floor, formData);

                    if (properties.floorChanged) {
                        properties.floorChanged({
                            ...properties.floor, floorplanImageURI: imageList[0].file.name,
                            floorplanImageWidth: img.width, floorplanImageHeight: img.height
                        });
                    }

                    setImages([]);
                };
            }
        } catch (error: any) {
            //pushError(error);
        }
    };

    /** Remember the current view centre as an offset from the plan's centre (resize-safe). */
    const rememberPan = () => {
        const rasterCenter = rasterCenterRef.current;
        if (paper.view && rasterCenter) {
            panZoomRef.current.panOffset = paper.view.center.subtract(rasterCenter);
        }
    };

    /**
     * Zoom that shows the whole plan in the canvas as it is *measured* right now.
     * The old calculation divided by a literal 800 while the view was the size of the
     * image itself, so the plan came up far too large on every screen.
     */
    const fitZoom = useCallback(() => {
        const imageWidth = rasterSizeRef.current?.width || floor?.floorplanImageWidth;
        const imageHeight = rasterSizeRef.current?.height || floor?.floorplanImageHeight;

        if (!imageWidth || !imageHeight || !canvasSize.width || !canvasSize.height) {
            return 1;
        }

        // The whole plan has to fit both dimensions, so the smaller ratio wins; the
        // margin keeps the outline off the clipped edge.
        return Math.min(canvasSize.width / imageWidth, canvasSize.height / imageHeight);
    }, [floor?.floorplanImageWidth, floor?.floorplanImageHeight, canvasSize.width, canvasSize.height]);

    /**
     * Single zoom entry point for wheel, pinch and the toolbar buttons.
     * `anchorViewPoint` (canvas pixels) stays put while the plan scales around it;
     * without one the canvas centre is used.
     */
    const applyZoom = useCallback((requestedZoom: number, anchorViewPoint?: any) => {
        if (!paper.view || !requestedZoom || !isFinite(requestedZoom)) {
            return;
        }

        const oldZoom = paper.view.zoom;
        const fit = fitZoom();
        // Bounded relative to the fit so a plan can never be zoomed into nothing
        // or blown up past any usable detail.
        const newZoom = Math.max(fit * 0.1, Math.min(fit * 50, requestedZoom));

        if (newZoom === oldZoom) {
            return;
        }

        const beta = oldZoom / newZoom;
        const anchor = anchorViewPoint || new paper.Point(canvasSize.width / 2, canvasSize.height / 2);

        // viewToProject() gives the coordinates in the Project space from the Screen Coordinates

        const mpos = paper.view.viewToProject(anchor);
        const center = paper.view.center;
        const pc = mpos.subtract(center);
        const offset = mpos.subtract(pc.multiply(beta)).subtract(center);

        paper.view.zoom = newZoom;
        panZoomRef.current.zoom = newZoom;
        paper.view.center = paper.view.center.add(offset);
        rememberPan();
        userAdjustedViewRef.current = true;

        // Keep marker icons + position pins a constant on-screen size (only the floorplan and
        // spaces should zoom). Counter-scale each marker around its pin by the inverse zoom step.
        rescaleMarkers(oldZoom, newZoom);
        rescaleVertexHandles(oldZoom, newZoom);

        // @ts-ignore
        paper.view.draw();
        // rememberPan/rescaleMarkers only touch refs, so they are not dependencies.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitZoom, canvasSize.width, canvasSize.height]);

    /** Show the whole plan again, centred — the way it comes up on first load. */
    const fitToWindow = useCallback(() => {
        if (!paper.view || !rasterCenterRef.current) {
            return;
        }

        const oldZoom = paper.view.zoom;
        const newZoom = fitZoom();

        paper.view.zoom = newZoom;
        panZoomRef.current.zoom = newZoom;
        paper.view.center = rasterCenterRef.current;
        panZoomRef.current.panOffset = new Point(0, 0);
        userAdjustedViewRef.current = false;

        rescaleMarkers(oldZoom, newZoom);
        rescaleVertexHandles(oldZoom, newZoom);

        // @ts-ignore
        paper.view.draw();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fitZoom]);

    const panZoom = (event: any) => {
        if (!paper.view || !canvasRef.current) {
            return;
        }

        // Vorzeichen und Richtung stehen in `zoomFactorFor` — dort steht auch, warum ein
        // Trackpad-Kneifen hier und nicht im Touch-Pfad ankommt.
        const factor = zoomFactorFor(event.deltaY);

        // React's synthetic wheel event does not expose offsetX/offsetY, so
        // `event.offsetX` was undefined → Point(0,0) and every zoom anchored to
        // the top-left corner. Derive the cursor position from clientX/clientY
        // relative to the canvas instead.
        const canvasRect = canvasRef.current.getBoundingClientRect();

        applyZoom(
            paper.view.zoom * factor,
            new paper.Point(event.clientX - canvasRect.left, event.clientY - canvasRect.top)
        );
    };

    // --- Touch: two-finger pinch zoom -------------------------------------------------
    // A wheel is the only zoom a mouse has, and it is the only zoom this editor had —
    // which left an iPad with no way to zoom out at all. A pinch feeds the same zoom
    // math as the wheel; single-finger drags stay with paper.js (pan / draw).

    const touchDistance = (touches: any) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;

        return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = useCallback((event: any) => {
        if (event.touches.length !== 2) {
            return;
        }

        pinchRef.current = { active: true, distance: touchDistance(event.touches) };
        pinchDrawingGuardRef.current?.();
        event.preventDefault();
    }, []);

    const onTouchMove = useCallback((event: any) => {
        if (!pinchRef.current.active || event.touches.length !== 2 || !paper.view) {
            return;
        }

        event.preventDefault();

        const distance = touchDistance(event.touches);
        const previousDistance = pinchRef.current.distance;

        if (!distance || !previousDistance) {
            return;
        }

        pinchRef.current.distance = distance;

        const canvasRect = canvasRef.current?.getBoundingClientRect();
        const midpoint = canvasRect
            ? new paper.Point(
                (event.touches[0].clientX + event.touches[1].clientX) / 2 - canvasRect.left,
                (event.touches[0].clientY + event.touches[1].clientY) / 2 - canvasRect.top
            )
            : undefined;

        applyZoom(paper.view.zoom * (distance / previousDistance), midpoint);
    }, [applyZoom]);

    const onTouchEnd = useCallback((event: any) => {
        if (event.touches.length < 2) {
            pinchRef.current.active = false;
        }
    }, []);

    // Registered natively (not via React's synthetic events) because the pinch has to
    // call preventDefault, which needs a non-passive listener.
    useEffect(() => {
        if (!canvasNode) {
            return;
        }

        canvasNode.addEventListener('touchstart', onTouchStart, { passive: false });
        canvasNode.addEventListener('touchmove', onTouchMove, { passive: false });
        canvasNode.addEventListener('touchend', onTouchEnd);
        canvasNode.addEventListener('touchcancel', onTouchEnd);

        return () => {
            canvasNode.removeEventListener('touchstart', onTouchStart);
            canvasNode.removeEventListener('touchmove', onTouchMove);
            canvasNode.removeEventListener('touchend', onTouchEnd);
            canvasNode.removeEventListener('touchcancel', onTouchEnd);
        };
    }, [canvasNode, onTouchStart, onTouchMove, onTouchEnd]);

    // Letzte Zeigerposition in Fensterkoordinaten. Sie kommt aus dem DOM, nicht aus paper:
    // dessen Werkzeug-Ereignisse tragen je nach Quelle (Maus, Touch, synthetisch) kein
    // natives Ereignis mit clientX/clientY, und das Menü stand dann in der Ecke bei 0/0.
    useEffect(() => {
        if (!canvasNode) {
            return;
        }

        const remember = (event: MouseEvent | TouchEvent) => {
            const point: any = (event as TouchEvent).touches?.[0] ?? event;

            if (typeof point?.clientX === 'number') {
                panZoomRef.current.pointerScreen = { x: point.clientX, y: point.clientY };
            }
        };

        canvasNode.addEventListener('mousedown', remember);
        canvasNode.addEventListener('touchstart', remember, { passive: true });

        return () => {
            canvasNode.removeEventListener('mousedown', remember);
            canvasNode.removeEventListener('touchstart', remember);
        };
    }, [canvasNode]);

    // Measure the container and drive the canvas' pixel size from it, so the editor
    // fills the space it is given and refits when that space changes (window resize,
    // sidebar, iPad rotation).
    useEffect(() => {
        if (!canvasContainer) {
            return;
        }

        const measure = () => {
            const width = Math.max(1, Math.round(canvasContainer.clientWidth));
            const height = Math.max(1, Math.round(canvasContainer.clientHeight));

            setCanvasSize((previous) =>
                previous.width === width && previous.height === height ? previous : { width, height }
            );
        };

        // A new size rebuilds the paper project, so a window being dragged smaller
        // must not measure once per frame.
        let debounce: any = null;
        const measureSoon = () => {
            if (debounce) {
                clearTimeout(debounce);
            }

            debounce = setTimeout(measure, 150);
        };

        measure();

        if (typeof ResizeObserver === 'undefined') {
            window.addEventListener('resize', measureSoon);

            return () => {
                clearTimeout(debounce);
                window.removeEventListener('resize', measureSoon);
            };
        }

        const observer = new ResizeObserver(measureSoon);

        observer.observe(canvasContainer);

        return () => {
            clearTimeout(debounce);
            observer.disconnect();
        };
    }, [canvasContainer]);

    const itemWithAssetData = (item: any): any => {
        if (item.data.asset) {
            return item;
        } else if (!item.parent) {
            return null;
        }

        return itemWithAssetData(item.parent);
    }

    const assetOffsetX = 30;
    const assetOffsetY = 60;
    const assetIconWidth = 30;

    useEffect(() => {
        setFloor({ ...properties.floor, floorplanImageScale: properties.floor.floorplanImageScale || 1 });
    }, [properties.floor]);

    // Reset the loaded flag when the image URI changes so the new image's
    // onLoad → setFloorplaImageLoaded(true) actually triggers a re-render and
    // re-runs the canvas-draw effect. Without this, replacing a floorplan
    // never redraws because the flag was already `true`.
    useEffect(() => {
        setFloorplaImageLoaded(false);
        setFloorplanImageFailed(false);
        // A replacement plan brings its own dimensions. The fit calculation runs
        // only once (`zoom < 0`), so without this reset the new raster inherits
        // the previous plan's zoom and pan and can end up entirely outside the
        // clipped viewport — the plan looks like it never arrived.
        panZoomRef.current.zoom = -1;
        panZoomRef.current.panOffset = new Point(0, 0);
        rasterCenterRef.current = null;
        rasterSizeRef.current = null;
        rasterRef.current = null;
        forcedRedrawsRef.current = 0;
        userAdjustedViewRef.current = false;
    }, [properties.floor?.floorplanImageURI]);

    useEffect(() => {
        if (properties.spaces) {
            setSpaces(properties.spaces)
        } else {

            setSpaces([]);
        }
    }, [properties.spaces]);

    useEffect(() => {
        setSelectedSpaceIndex(properties.selectedSpaceIndex)
    }, [properties.selectedSpaceIndex]);

    useEffect(() => {
        const next = properties.assets || [];
        // Callers may pass a fresh array (e.g. `assets={[]}`) on every render.
        // Keep the previous state reference when the contents are unchanged so
        // the canvas-draw effect doesn't re-run (and rebuild paper.js) needlessly.
        setAssets((prev: any[]) =>
            prev.length === next.length && prev.every((a: any, i: number) => a === next[i])
                ? prev
                : next
        );
    }, [properties.assets]);

    // New status styles only restyle the drawn markers; rebuilding the plan would reset the
    // view and cost a full redraw on every status refresh or filter click.
    useEffect(() => {
        markerStylesRef.current = properties.assetMarkerStyles;
        restyleMarkers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [properties.assetMarkerStyles]);

    // Muting the rooms redraws only the rooms (the markers stay on top, see drawRooms).
    useEffect(() => {
        if (muteRoomsRef.current === !!properties.muteRooms) {
            return;
        }
        muteRoomsRef.current = !!properties.muteRooms;
        drawRoomsRef.current?.(spacesRef.current);
        paper.view?.update?.();
    }, [properties.muteRooms]);

    useEffect(() => {
        if (!canvasRef || !canvasRef.current || !floor || !floor.floorplanImageWidth || !floor.floorplanImageHeight || !floorplanImageLoaded) {
            return;
        }

        // Check if the image element exists and is loaded
        const imageElement = document.getElementById('floorplan') as HTMLImageElement;
        if (!imageElement || !imageElement.complete || imageElement.naturalWidth === 0) {
            console.log('Image not ready yet, waiting...');
            return;
        }

        // `paper.setup` skaliert die Zeichenfläche für HiDPI, indem es die width/height-Attribute
        // des Canvas mit dem Gerätefaktor multipliziert. Läuft der Effekt erneut, ohne dass React
        // die Attribute neu schreibt (etwa nach ESC: nur `redrawTick` ändert sich), multipliziert
        // paper die bereits vergrößerten Werte ein zweites Mal — die Ansicht wächst um den Faktor
        // vier und der Plan liegt außerhalb des Sichtbaren. Deshalb vor jedem Setup auf die
        // gemessene Größe zurücksetzen.
        if (canvasRef.current) {
            canvasRef.current.width = canvasSize.width;
            canvasRef.current.height = canvasSize.height;
        }

        //paper.install(window);
        paper.setup('editorCanvas');

        // `paper.setup` skaliert für HiDPI und schreibt dabei feste Pixelmaße in den Stil des
        // Canvas — der stünde danach in seiner eigenen Größe statt in der seiner Box. Die
        // Zeichenfläche (width/height) bleibt bei den gemessenen Pixeln, die Darstellung füllt
        // die Box.
        if (canvasNode) {
            canvasNode.style.width = '100%';
            canvasNode.style.height = '100%';
        }

        // Draw floorplan
        let cancelled = false;
        let raster: any;
        let tool: any = null;
        let keyDownHandler: ((event: KeyboardEvent) => void) | null = null;

        const cleanup = () => {
            cancelled = true;
            if (raster) {
                raster.onLoad = null;
            }
            if (keyDownHandler) {
                document.removeEventListener('keydown', keyDownHandler);
                drawingActionsRef.current = null;
                keyDownHandler = null;
            }
            pinchDrawingGuardRef.current = null;
            drawRoomsRef.current = null;
            paper.project.remove();
            tool?.remove();
        };

        try {
            raster = new paper.Raster('floorplan');

            // Large images can be constructed before paper reports `loaded`.
            // Rather than give up (leaving the canvas blank after paper.setup
            // cleared it), redraw once the raster fires its load event.
            if (!raster.loaded) {
                raster.onLoad = () => {
                    if (!cancelled) {
                        setRedrawTick((t) => t + 1);
                    }
                };

                // Das Bild im DOM ist fertig, sein `load` ist längst vorbei: dann kommt das
                // Ereignis oben nie. Begrenzt neu anlaufen lassen, statt leer stehen zu bleiben.
                if (imageElement.complete && imageElement.naturalWidth > 0 && forcedRedrawsRef.current < 3) {
                    forcedRedrawsRef.current += 1;

                    requestAnimationFrame(() => {
                        if (!cancelled) {
                            setRedrawTick((t) => t + 1);
                        }
                    });
                }

                return cleanup;
            }

            // Angekommen: Das Bild ist da, also war der Anlauf erfolgreich.
            //
            // Ohne dieses Zurücksetzen ist das Budget oben eine Zählung über die Lebensdauer der
            // Seite statt über einen hängenden Ladevorgang: Jeder Neuaufbau der Szene — und jedes
            // Speichern einer Raumform ist einer — verbraucht einen Versuch, und nach dem dritten
            // bleibt die Zeichenfläche leer. Gemeint war „gib es nach drei vergeblichen Anläufen
            // auf", nicht „nach drei Neuaufbauten überhaupt".
            forcedRedrawsRef.current = 0;

            raster.position = paper.view.center;
            raster.scale(1);

            rasterCenterRef.current = raster.position.clone();
            rasterSizeRef.current = { width: raster.bounds.width, height: raster.bounds.height };
            // Woran die Ausstiche hängen: Sie gehören unmittelbar über das Bild und unter alles,
            // was daraufgezeichnet ist — sie nehmen etwas aus dem Plan, nicht aus den Räumen.
            rasterRef.current = raster;

            if (raster.bounds.width > 0) {
                const aspect = raster.bounds.height / raster.bounds.width;

                setPlanAspect((previous) =>
                    previous && Math.abs(previous - aspect) < 0.001 ? previous : aspect,
                );
            }

            // // Initialization of panZoom

            if (panZoomRef.current.zoom < 0 || !userAdjustedViewRef.current) {
                // Fit the whole plan into the canvas as it is measured now. This runs
                // again on every canvas resize as long as nobody has zoomed or panned,
                // so a narrower column or a rotated iPad still shows the whole plan.
                panZoomRef.current.zoom = fitZoom();
                panZoomRef.current.panOffset = new Point(0, 0);
            }

            panZoomRef.current.imageOffset = new Point(raster.bounds.left, raster.bounds.top);

            // Set or restore zoom. The pan is restored relative to the plan's centre,
            // so a resized canvas keeps looking at the same part of the plan.

            paper.view.zoom = panZoomRef.current.zoom;
            paper.view.center = rasterCenterRef.current.add(panZoomRef.current.panOffset);

            // Draw origin

            const originSize = 20; // * panZoomRef.current.zoom;

            let originPath = new paper.Path.Line(new Point(raster.bounds.left - originSize / 2, raster.bounds.top), new Point(new Point(raster.bounds.left + originSize / 2, raster.bounds.top)));

            // @ts-ignore
            originPath.strokeColor = 'green';

            originPath = new paper.Path.Line(new Point(raster.bounds.left, raster.bounds.top - originSize / 2), new Point(new Point(raster.bounds.left, raster.bounds.top + originSize / 2)));

            // @ts-ignore
            originPath.strokeColor = 'green';
        } catch (error) {
            console.error('Error creating raster:', error);
            return cleanup;
        }

        // Only continue if raster was successfully created
        if (!raster) {
            console.error('Raster not available, skipping drawing');
            return cleanup;
        }

        // Draw spaces

        /**
         * Die Räume zeichnen — als eigene Funktion, damit eine Formänderung sie erneuern kann,
         * **ohne** die Szene neu aufzubauen.
         *
         * Der Neuaufbau nimmt das Projekt mitsamt Rasterbild auseinander und setzt es wieder
         * zusammen; das ist für ein verschobenes Eck viel zu viel und war die Quelle der leeren
         * Zeichenfläche. Das Bild ändert sich beim Bearbeiten einer Raumform nicht.
         */
        const drawRooms = (current: Space[]) => {
            for (const path of roomPathsRef.current) {
                try {
                    path.remove();
                } catch {
                    // Schon fort; dann ist nichts zu tun.
                }
            }

            roomPathsRef.current = [];

            (current ?? []).forEach((space: Space, index: number) => {
            // The room being (re)drawn used to be skipped entirely, so it vanished from
            // the plan the moment drawing started — it read as "the room was deleted".
            // It stays on as a dashed, unfilled outline instead: visible as a reference,
            // clearly not a finished room, and not clickable (a click is a polygon point).
            const editing = index === selectedSpaceIndex;

            // shape is a JSON-typed field; some records arrive as a JSON string
            // from the Mongo backend, others as an already-decoded array. Coerce
            // to an array (or skip if the value is unusable) so the canvas
            // drawing doesn't crash the whole editor.
            let shape: any[] = Array.isArray(space.shape)
                ? (space.shape as any[])
                : (typeof space.shape === 'string'
                    ? (() => {
                        try {
                            const parsed = JSON.parse(space.shape as unknown as string);
                            return Array.isArray(parsed) ? parsed : [];
                        } catch {
                            return [];
                        }
                    })()
                    : []);

            if (shape.length > 2) {
                const path = new paper.Path();

                roomPathsRef.current.push(path);
                const projectShape = shape.map((point: any) => toProjectCoordinates(point, floor.floorplanImageScale, raster.bounds.left, raster.bounds.top));

                // Guard against oversized "spaces": some DWG imports produce a
                // handful of records whose polygon spans most of the floor (e.g.
                // duplicated comb-shaped zone outlines). Filled opaquely they
                // cover the whole floorplan and every other room, leaving a
                // single flat colour. A real room never covers most of the plan,
                // so anything past this threshold is drawn as an outline only.
                const xs = projectShape.map((p: any) => p.x);
                const ys = projectShape.map((p: any) => p.y);
                const bboxArea =
                    (Math.max(...xs) - Math.min(...xs)) *
                    (Math.max(...ys) - Math.min(...ys));
                const imageArea = Math.abs(raster.bounds.width * raster.bounds.height);
                const oversized = imageArea > 0 && bboxArea > 0.5 * imageArea;

                // Only reasonably-sized spaces with an explicit colour get a
                // filled tint; uncoloured or oversized ones get just a faint
                // outline so the underlying floorplan shows through.
                // @ts-ignore
                path.strokeColor = space.color || 'rgba(173, 164, 217, 0.5)';
                // @ts-ignore
                path.fillColor = (oversized || editing) ? null : (space.color || null);

                // Devices tab: rooms fade to a faint grey wash so the status markers stand out.
                if (muteRoomsRef.current && !editing) {
                    path.strokeColor = new paper.Color(0.45, 0.45, 0.45, 0.35);
                    if (path.fillColor) {
                        try {
                            const wash = new paper.Color(space.color as any);
                            wash.saturation = wash.saturation * 0.2;
                            wash.alpha = 0.18;
                            path.fillColor = wash;
                        } catch {
                            path.fillColor = new paper.Color(0.5, 0.5, 0.5, 0.1);
                        }
                    }
                }
                // Outlines are measured in screen pixels, not project units: at a
                // fit-to-window zoom a project-scaled 1px stroke is a fraction of a
                // pixel and the room borders disappear.
                path.strokeScaling = false;
                path.strokeWidth = editing ? 2 : 1;

                if (editing) {
                    path.dashArray = [6, 4];
                }

                (projectShape || []).forEach((point: any) => path.add(new Point(point.x, point.y)));

                path.add(new Point(projectShape[0].x, projectShape[0].y));

                // Add interaction

                if (!editing) {
                    path.onClick = function (event: any) {
                        /*
                         * Ein Zug ist kein Klick — auch wenn paper ihn so meldet.
                         *
                         * paper feuert `click` an einem Objekt, sobald Drücken und Loslassen über
                         * ihm geschehen; eine Bewegungsschwelle wie im DOM gibt es nicht. Wer den
                         * Ausschnitt über einem Raum verschiebt, wählte damit die Auswahl ab.
                         */
                        if (panZoomRef.current.panned) {
                            return false;
                        }

                        // Wer den Stempel führt, sticht aus — und wählt nicht nebenbei den Raum
                        // aus, über dem er gerade steht.
                        if (stencilRef.current.active) {
                            return false;
                        }

                        // A tap on a device marker belongs to the marker (its action menu), not to
                        // the room underneath — even if the room was drawn over it.
                        if (markerGroupsRef.current.some((group: any) => {
                            try {
                                return !!group?.project && !!group.hitTest(event.point, { fill: true, stroke: true, tolerance: 2 });
                            } catch {
                                return false;
                            }
                        })) {
                            return false;
                        }

                        /*
                         * Liegt ein Eckgriff unter dem Zeiger, gehört der Klick ihm.
                         *
                         * Der Griff sitzt auf der Umrisslinie; ohne dies höbe ein Tipp auf eine
                         * Ecke die Auswahl des Raums auf — und die Ecke verschwände mit ihr. Das
                         * Ausweichen steht hier und nicht als `onClick` am Griff: Ein Handler dort,
                         * der `false` zurückgibt, hält in paper die ganze Ereigniskette an und
                         * nimmt dem Griff seinen Doppelklick.
                         */
                        const editing = vertexEditRef.current;

                        if (editing?.handles?.some((handle: any) => handle.hitTest(event.point))) {
                            return false;
                        }

                        /*
                         * Wurde gerade eine Ecke gezogen, gehört das Release nicht dem Raum.
                         *
                         * Das Einrasten zieht die Ecke aus der Zeigerspur, also liegt der Griff
                         * beim Loslassen nicht mehr unter dem Zeiger — der Test oben scheitert.
                         * Ohne diese Prüfung wechselt der Klick zum Raum, der wird abgewählt, und
                         * die Griffe verschwinden unmittelbar nach dem Speichern.
                         */
                        if (editing?.moved) {
                            return false;
                        }

                        // Die Auswahl liegt im Zustand, nicht in `this.selected`: An einer
                        // Eigenschaft des Pfades hängt kein Neuzeichnen, und die Eckgriffe müssen
                        // erscheinen und verschwinden, wenn sie sich ändert.
                        selectRoomVertices(vertexEditIndexRef.current === index ? -1 : index);

                        // Tell the page which room was hit. The plan is the natural
                        // place to point at a room — the table is where its data
                        // lives — and without this the two views sit next to each
                        // other without talking.
                        properties.spaceClicked?.(index);

                        return false;
                    }
                }
            }
            });

            /*
             * Device markers stay on top of the rooms.
             *
             * Rooms are redrawn on their own (shape edits, stencil cuts, and rooms that arrive only
             * after the markers were drawn). Every redraw appends the room paths at the top of the
             * layer, over the markers. A tap on a marker then hit the filled room, which selected
             * the room and showed the corner-editing hint.
             */
            for (const group of markerGroupsRef.current) {
                try {
                    if (group?.project) {
                        group.bringToFront();
                    }
                } catch {
                    // Removed mid-rebuild; nothing to do.
                }
            }
        };

        drawRoomsRef.current = drawRooms;
        drawRooms(spacesRef.current);

        // The view is zoomed to fit the (large) floorplan to the 800px canvas, so
        // marker geometry in project units would render only a few px. Scale all
        // marker sizes/offsets by 1/zoom so they show at a readable, constant
        // on-screen size at the default fit.
        const markerScale = paper.view.zoom > 0 ? 1 / paper.view.zoom : 1;
        const offX = assetOffsetX * markerScale;
        const offY = assetOffsetY * markerScale;
        const iconW = assetIconWidth * markerScale;

        const drawAssetMarkers = async () => {
            markerGroupsRef.current = [];
            const placeable = assets.filter(
                (asset: any) =>
                    asset.x !== undefined && asset.y !== undefined && (asset.name || asset.id)
            );

            // Markers are drawn with the DIN 276 category icon (a Material Symbol, rendered as canvas
            // text). Load that font once so paper measures/renders the glyph (not the literal name).
            const msFontReady = await loadIconFontForCanvas();

            // SVG fallback (only for assets without a category icon): preload each DISTINCT icon's SVG
            // once (was one XHR per asset → thousands of concurrent requests on large floors).
            const distinctIconIds = Array.from(
                new Set(
                    placeable
                        .filter((asset: any) => !(asset.categoryIcon && msFontReady))
                        .map(
                            (asset: any) =>
                                (getAssetIcon(asset.icon || '') ||
                                    getAssetIcon('fire-extinguisher')) as any
                        )
                        .filter(Boolean)
                        .map((icon: any) => icon.id)
                )
            );
            await Promise.all(distinctIconIds.map((id: any) => preloadIconSvg(String(id))));

            // A newer redraw (or unmount) may have torn down this project while we awaited.
            if (cancelled || !raster) {
                return;
            }

            // Import each distinct icon once as an off-canvas template; clone it per asset
            // (cheap, no re-parse, no network).
            const iconTemplates = new Map<string, any>();
            const iconTemplate = (id: string) => {
                if (iconTemplates.has(id)) {
                    return iconTemplates.get(id);
                }
                const text = svgTextCache.get(id);
                let template: any = null;
                if (text) {
                    template = paper.project.importSVG(text, { insert: false });
                }
                iconTemplates.set(id, template);
                return template;
            };

            // Vertical centring for Material Symbol glyphs: paper's PointText bounds don't tightly
            // wrap a font glyph, so we measure the real glyph ascent/descent (once per distinct
            // symbol, cheap) and drop the baseline by (ascent − descent)/2 to centre it in the box.
            const glyphFontSize = iconW * 0.8;
            const measureCtx = document.createElement('canvas').getContext('2d');
            const glyphBaselineOffset = new Map<string, number>();
            const baselineOffset = (name: string): number => {
                const cached = glyphBaselineOffset.get(name);
                if (cached !== undefined) {
                    return cached;
                }
                let offset = glyphFontSize * 0.35;
                try {
                    if (measureCtx) {
                        measureCtx.font = `${glyphFontSize}px "Material Symbols Outlined"`;
                        const m = measureCtx.measureText(name);
                        const ascent = (m as any).actualBoundingBoxAscent ?? 0;
                        const descent = (m as any).actualBoundingBoxDescent ?? 0;
                        if (ascent || descent) {
                            offset = (ascent - descent) / 2;
                        }
                    }
                } catch {
                    // keep fallback
                }
                glyphBaselineOffset.set(name, offset);
                return offset;
            };

            for (const asset of placeable) {
                // Check if this effect run was cancelled while we loop.
                if (cancelled) {
                    return;
                }
                const assetLocation = toProjectCoordinates(new Point(asset.x, asset.y), floor.floorplanImageScale, raster.bounds.left, raster.bounds.top)
                const iconCenter = new Point(assetLocation.x + offX, assetLocation.y - offY);

                const line = new Path();

                // @ts-ignore
                line.strokeColor = 'black';
                // strokeScaling=false keeps a constant on-screen stroke width regardless of zoom
                // (paper otherwise thickens the line as the view — and our counter-scale — change).
                line.strokeScaling = false;
                line.strokeWidth = 1;
                line.add(assetLocation);
                line.add(iconCenter);

                const iconBox = new paper.Rectangle(
                    new Point(iconCenter.x - iconW / 2, iconCenter.y - iconW / 2),
                    new Point(iconCenter.x + iconW / 2, iconCenter.y + iconW / 2)
                );
                const path = new Path.Rectangle(iconBox);

                path.fillColor = new paper.Color(1, 1, 1);

                const icon: any = getAssetIcon(asset.icon || '') || getAssetIcon('fire-extinguisher');
                // Colour-code the marker by the asset's DIN 276 category (from metadata), not the
                // per-asset icon set; fall back to the icon's own colour when no category colour.
                const baseColor = asset.categoryColor || icon.color;
                const markerColor = baseColor;

                path.strokeColor = new paper.Color(markerColor);
                path.strokeScaling = false;
                path.strokeWidth = 1;

                const label = asset.name || asset.id;

                const text = new paper.PointText({
                    point: [iconCenter.x - 3 * label.length * markerScale, iconCenter.y - 25 * markerScale],
                    content: label,
                    fillColor: markerColor,
                    fontFamily: 'var(--brand-font-family)',
                    fontSize: 14 * markerScale
                });

                const circle = new Path.Circle(assetLocation, 3 * markerScale);

                circle.fillColor = new paper.Color(1, 1, 1);
                circle.strokeColor = new paper.Color(0, 0, 0);
                circle.strokeScaling = false;
                circle.strokeWidth = 1;
                circle.name = 'assetPosition';

                const members: any[] = [line, path, text, circle];
                let glyphItem: any = null;
                if (asset.categoryIcon && msFontReady) {
                    // Render the DIN 276 category icon as a Material Symbol glyph (canvas text),
                    // colour-coded and centred in the marker box. justification centres it
                    // horizontally; the baseline is dropped ~0.35·fontSize below the box centre so
                    // the glyph sits vertically centred (textBaseline:'middle' equivalent).
                    const glyph = new paper.PointText({
                        point: [iconCenter.x, iconCenter.y + baselineOffset(asset.categoryIcon)],
                        content: asset.categoryIcon,
                        fillColor: markerColor,
                        fontFamily: 'Material Symbols Outlined',
                        fontSize: glyphFontSize,
                        justification: 'center',
                    });
                    members.push(glyph);
                    glyphItem = glyph;
                } else {
                    // Fallback: the per-asset SVG glyph, fit into the (constant-screen-size) box.
                    const template = iconTemplate(icon.id);
                    if (template) {
                        const svgItem = template.clone();
                        svgItem.fitBounds(iconBox);
                        members.push(svgItem);
                    }
                }

                // Status cue: a small dot on the box's top-right corner with a glyph ("!", clock,
                // wrench …) — hidden until the asset has a status style (see `styleMarker`).
                const cueRadius = 7 * markerScale;
                const cueCenter = new Point(iconBox.right, iconBox.top);
                const cueDot = new Path.Circle(cueCenter, cueRadius);
                cueDot.strokeColor = new paper.Color(1, 1, 1);
                cueDot.strokeScaling = false;
                cueDot.strokeWidth = 1.5;
                cueDot.visible = false;
                const cueFontSize = 11 * markerScale;
                const cueText = new paper.PointText({
                    point: [
                        cueCenter.x,
                        cueCenter.y + (msFontReady
                            ? baselineOffset('schedule') * (cueFontSize / glyphFontSize)
                            : cueFontSize * 0.35),
                    ],
                    content: '',
                    fillColor: 'white',
                    fontFamily: msFontReady ? 'Material Symbols Outlined' : 'sans-serif',
                    fontWeight: 'bold',
                    fontSize: cueFontSize,
                    justification: 'center',
                });
                cueText.visible = false;
                members.push(cueDot, cueText);

                const group = new Group(members);

                // `anchor` (the pin position, project space) is the fixed point we scale around on
                // zoom so the marker keeps a constant screen size.
                group.data = {
                    asset,
                    anchor: assetLocation,
                    tint: {
                        baseColor,
                        box: path,
                        label: text,
                        glyph: glyphItem,
                        cueDot,
                        cueText,
                        fontReady: msFontReady,
                    },
                };
                markerGroupsRef.current.push(group);
                styleMarker(group);

                // Ein Tipp auf die Anlage fragt, was gewollt ist, statt ungefragt die Detailseite
                // zu öffnen (#735). Ohne Menü-Aufnehmer bleibt es beim alten Sprung, damit
                // Einbettungen ohne Aktionsmenü nicht ohne Reaktion dastehen.
                group.onClick = function (_event: any) {
                    // Der Stempel hat Vorrang: Ein Ausstich über einer Anlage soll nicht deren
                    // Detailseite öffnen.
                    if (stencilRef.current.active) {
                        return false;
                    }

                    // Mit Menü übernimmt der Werkzeug-Handler (er unterscheidet Tippen von Ziehen);
                    // ohne Menü bleibt es beim alten Sprung auf die Detailseite.
                    if (!properties.assetClicked) {
                        navigate(`/entity?selectedEntityType=Asset&selectedEntity=${asset._id}`);
                    }

                    return false;
                };
            }

            paper.view?.update?.();
        };
        void drawAssetMarkers();

        tool = new Tool();
        const hitOptions = {
            segments: true,
            stroke: true,
            fill: true,
            tolerance: 5
        };

        if (spacesRef.current && selectedSpaceIndex >= 0) {
            let path: any = null;
            const points: any = [];
            let lastPointAddedAt = 0;

            // The polygon being drawn carries the colour of the room it belongs to.
            // It used to be hardcoded lilac (and red while the rubber band followed the
            // cursor), which is why the room colour looked like it "only sometimes
            // applies": it appeared once the room was saved and redrawn from space.color.
            const drawingColor = (spacesRef.current[selectedSpaceIndex] || {}).color || 'rgb(173, 164, 217)';

            const toPaperColor = (value: string, alpha: number) => {
                try {
                    const color = new paper.Color(value);

                    color.alpha = alpha;

                    return color;
                } catch {
                    return new paper.Color(0.68, 0.64, 0.85, alpha);
                }
            };

            const styleDrawingPath = (target: any) => {
                target.fillColor = toPaperColor(drawingColor, 0.25);
                target.strokeColor = toPaperColor(drawingColor, 0.9);
                target.strokeScaling = false;
                target.strokeWidth = 2;
            };

            /** Redraw the in-progress polygon from `points`. */
            const redrawDrawingPath = () => {
                path?.remove();
                path = null;

                if (points.length === 0) {
                    return;
                }

                path = new Path();

                styleDrawingPath(path);

                points.forEach((point: any) => path.add(point));
            };

            // Only the point the pinching finger just placed is taken back — a point
            // set earlier on purpose stays.
            pinchDrawingGuardRef.current = () => {
                if (points.length === 0 || Date.now() - lastPointAddedAt > 500) {
                    return;
                }

                points.pop();
                redrawDrawingPath();
            };

            // The close-the-polygon target is 10 screen pixels wide, so it has to be
            // converted out of project units — at a fit-to-window zoom a fixed 10
            // project units is far too small to hit.
            const closeTolerance = () => 10 / (paper.view?.zoom || 1);

            /** Close the polygon, store the room and leave drawing mode. */
            const finishPolygon = () => {
                if (points.length < 3) {
                    return;
                }

                setDrawnPointCount(0);
                setRedrawTick((tick) => tick + 1);

                if (path) {
                    path.add(points[0]);
                }

                path = null;

                const buildingPoints: any = points.map((point: any) => toBuildingCoordinates(point, floor.floorplanImageScale, raster.bounds.left, raster.bounds.top));

                const previousSpace = spacesRef.current[selectedSpaceIndex];
                const newSpace = { ...previousSpace, area: getConvexPolygonArea(buildingPoints) / 1000000, shape: buildingPoints }

                console.log('Changed Space >>>', newSpace);

                // Auch ein neu gezeichneter Raum ist ein Schritt: Wer sich verzeichnet, hätte sonst
                // nur den Weg, ihn noch einmal zu zeichnen.
                rememberEdit({
                    label: t('ui.floorplanRoomShapeChanged'),
                    undo: () => applySpaceRef.current(previousSpace),
                    redo: () => applySpaceRef.current(newSpace),
                });

                updateSpaceRef.current(newSpace).then(() => {
                    const newSpaces = [...spacesRef.current]

                    newSpaces[selectedSpaceIndex] = newSpace;

                    setSpaces(newSpaces);
                    setSelectedSpaceIndex(-1);

                    if (properties.spacesChanged) {
                        properties.spacesChanged();
                    }

                    if (properties.floorChanged) {
                        properties.floorChanged({ ...properties.floor, spaces: newSpaces });
                    }

                    if (properties.selectedSpaceIndexChanged) {
                        properties.selectedSpaceIndexChanged(-1);
                    }
                });
            };

            /** Drop what was drawn and leave drawing mode; the stored room is untouched. */
            const cancelDrawing = () => {
                setDrawnPointCount(0);
                // Ohne diesen Anstoß bliebe die Fläche nach ESC leer, bis Zoom oder
                // "Ansicht passend" zufällig neu zeichnen (#735).
                setRedrawTick((tick) => tick + 1);
                path?.remove();
                path = null;
                points.length = 0;

                setSelectedSpaceIndex(-1);

                if (properties.selectedSpaceIndexChanged) {
                    properties.selectedSpaceIndexChanged(-1);
                }
            };

            // Die Leiste bedient dieselben Aktionen wie ENTER/ESC — ein iPad hat keine davon.
            drawingActionsRef.current = { finish: finishPolygon, cancel: cancelDrawing };

            // @ts-ignore
            tool.onMouseDown = (event: any) => {
                const point = {
                    x: event.point.x,
                    y: event.point.y
                };

                if (points.length === 0) {
                    path = new Path();

                    styleDrawingPath(path);

                    points.push(point)
                    lastPointAddedAt = Date.now();
                    path.add(point);
                    setDrawnPointCount(points.length);
                } else {
                    if (Math.sqrt(Math.pow(points[0].x - point.x, 2) + Math.pow(points[0].y - point.y, 2)) < closeTolerance()) {
                        finishPolygon();
                    } else {
                        path.add(point);
                        points.push(point)
                        lastPointAddedAt = Date.now();
                        setDrawnPointCount(points.length);
                    }
                }
            };

            tool.onMouseMove = (event: any) => {
                if (!path || points.length === 0) {
                    return;
                }

                path.remove();
                path = new Path();

                styleDrawingPath(path);

                points.forEach((point: any) => path.add(point));

                const point = {
                    x: event.point.x,
                    y: event.point.y
                };

                path.add(point);
            };

            // Until now the only way out of drawing mode was hitting the first point
            // within a few pixels — with no way to confirm or to back out.
            keyDownHandler = (event: KeyboardEvent) => {
                const target = event.target as HTMLElement | null;

                // Never steal ENTER/ESC from a field the user is typing in.
                if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                    return;
                }

                if (event.key === 'Enter') {
                    event.preventDefault();
                    finishPolygon();
                } else if (event.key === 'Escape') {
                    event.preventDefault();
                    cancelDrawing();
                }
            };

            document.addEventListener('keydown', keyDownHandler);
        } else if (scaling) {
            let path: any = null;
            let startPoint: any = null;

            tool.onMouseDown = (event: any) => {
                console.log('Scaling start >>>', event.point);

                if (!startPoint) {
                    path = new Path();

                    path.strokeColor = 'red';

                    startPoint = {
                        x: event.point.x,
                        y: event.point.y
                    };

                    path.add(startPoint);
                    path.add(startPoint);
                } else {
                    if (!path) {
                        return;
                    }

                    path.remove();
                    setScalingMeasurement(Math.sqrt(Math.pow(startPoint.x - event.point.x, 2) + Math.pow(startPoint.y - event.point.y, 2)));
                    setScalingDialogOpen(true);
                    setScaling(false);
                    distanceInputRef.current?.focus();
                }
            }

            tool.onMouseMove = (event: any) => {
                console.log('Scaling move >>>', event.point);

                if (!path) {
                    return;
                }

                path.remove();

                path = new Path();
                path.strokeColor = 'red';

                path.add(startPoint);
                path.add({
                    x: event.point.x,
                    y: event.point.y
                });
            }
        } else {
            // Asset drag or pan handler

            /**
             * Das Quadrat läuft dem Zeiger voraus, damit vor dem Klick zu sehen ist, was fortfällt.
             *
             * Ohne diese Vorschau ist ein Stempel fester Größe ein Ratespiel: Man sieht erst nach
             * dem Ausstechen, ob er zu klein war oder zu viel mitgenommen hat.
             */
            tool.onMouseMove = (event: any) => {
                const preview = stencilPreviewRef.current;

                if (preview && preview.project) {
                    preview.position = event.point;
                    paper.view?.update?.();
                }
            };

            tool.onMouseDown = (event: any) => {
                if (pinchRef.current.active) {
                    return;
                }

                panZoomRef.current.panned = false;

                // Der Stempel nimmt das Drücken für sich: Ausgestochen wird beim Loslassen, damit
                // ein Verschieben des Ausschnitts weiterhin ein Verschieben bleibt.
                if (stencilRef.current.active) {
                    return;
                }

                // Ein Gerät wartet darauf, gesetzt zu werden: dieser Tipp ist sein Platz.
                const placing = placementRef.current;

                if (placing) {
                    placementRef.current = null;

                    const location = toBuildingCoordinates(
                        event.point,
                        floor.floorplanImageScale,
                        panZoomRef.current.imageOffset.x,
                        panZoomRef.current.imageOffset.y,
                    );
                    const placed = { ...placing, ...location, floor: properties.floor._id };

                    setAssets((current: any[]) =>
                        (current ?? []).some((asset: any) => asset._id === placed._id)
                            ? current.map((asset: any) => (asset._id === placed._id ? placed : asset))
                            // Aus der Bestandssuche gesetzt: das Gerät hing bisher an keiner Etage.
                            : [...(current ?? []), placed],
                    );

                    updateAsset(placed).then(() => {
                        if (properties.assetsChanged) {
                            properties.assetsChanged();
                        }

                        // Erst nach dem Speichern melden: eine Bestätigung, die schon vor dem
                        // Schreiben erscheint, behauptet etwas, das noch schiefgehen kann.
                        if (properties.placementSaved) {
                            properties.placementSaved(placed);
                        }
                    });

                    if (properties.placementFinished) {
                        properties.placementFinished();
                    }

                    return;
                }

                var hitResult = paper.project.hitTest(event.point, hitOptions);

                // Ein Eckgriff hat Vorrang: Er liegt über dem Raum, und wer ihn anfasst, will die
                // Ecke ziehen und nicht den Ausschnitt verschieben.
                const handleHit = hitResult?.item?.data?.vertexHandle ? hitResult.item : null;

                if (handleHit && vertexEditRef.current) {
                    panZoomRef.current.dragVertexIndex = handleHit.data.vertexIndex;
                    panZoomRef.current.vertexDragged = false;
                    panZoomRef.current.dragAssetItem = null;

                    return;
                }

                if (!!hitResult && !!hitResult.item) {
                    panZoomRef.current.dragAssetItem = itemWithAssetData(hitResult.item.parent);
                    panZoomRef.current.assetDragged = false;
                    const native: any = event?.event;

                    if (typeof native?.clientX === 'number') {
                        panZoomRef.current.pointerScreen = { x: native.clientX, y: native.clientY };
                    }
                }
            }

            // tool.onMouseDrag = function (event: any) {
            //     const point = new Point(event.event.offsetX, event.event.offsetY);
            //     const panOffset = panZoomRef.current.lastPoint ? point.subtract(panZoomRef.current.lastPoint) : new Point(0, 0);

            //     panZoomRef.current.lastPoint = point;
            //     paper.view.center = paper.view.center.subtract(new Point({ x: panOffset.x, y: panOffset.y }));
            //     panZoomRef.current.pan = paper.view.center;
            // }

            tool.onMouseDrag = (event: any) => {
                // A pinch is zoom only — paper still reports the first finger as a drag.
                if (pinchRef.current.active) {
                    return;
                }

                var pan_offset = event.point.subtract(event.downPoint);

                const editing = vertexEditRef.current;

                if (editing && panZoomRef.current.dragVertexIndex >= 0) {
                    /*
                     * Erst ab einer Mindeststrecke ein Zug — dieselbe Schwelle wie beim Ziehen
                     * einer Anlage.
                     *
                     * Ohne sie zählt das Wackeln von ein, zwei Pixeln beim Klicken schon als
                     * Bewegung: Das Antippen einer Ecke wählte sie dann nicht aus, sondern
                     * verschob sie um nichts und schrieb das auch noch weg.
                     */
                    const travelled = event.point.subtract(event.downPoint).length * (paper.view?.zoom || 1);

                    if (!panZoomRef.current.vertexDragged && travelled < DRAG_THRESHOLD_PX) {
                        return;
                    }

                    panZoomRef.current.vertexDragged = true;

                    const at = panZoomRef.current.dragVertexIndex;
                    const shape = editing.working;
                    const prev = shape[(at - 1 + shape.length) % shape.length];
                    const next = shape[(at + 1) % shape.length];

                    /*
                     * Auf den rechten Winkel einschnappen — es sei denn, jemand hält Wahl gedrückt.
                     *
                     * An, weil Grundrisse fast durchweg rechtwinklig sind: Der Normalfall soll ohne
                     * Zutun stimmen, und wer den schiefen Winkel wirklich will, bekommt ihn mit
                     * einem Finger.
                     *
                     * Gefangen wird über den **Winkel**. Ein Abstand in Pixeln ergäbe ein schmales
                     * Band um den Thaleskreis: Beim Ziehen kreuzt man es kurz, das Zeichen blitzt
                     * auf und ist wieder fort. Ein Toleranzwinkel ergibt einen Keil, der mit dem
                     * Raum mitwächst — dort hält die Ecke, und wer loslässt, während das Zeichen
                     * steht, bekommt genau 90°.
                     */
                    const snapped = event.modifiers?.option
                        ? null
                        : snapToRightAngle({ x: event.point.x, y: event.point.y }, prev, next, SNAP_TOLERANCE_DEGREES);

                    // Unmittelbar auf den paper-Objekten, ohne Speichern und ohne Neuaufbau: Ein
                    // Zug ist eine Bewegung, kein Dutzend Schreibvorgänge.
                    editing.working = moveVertex(editing.working, at, snapped ?? {
                        x: event.point.x,
                        y: event.point.y,
                    });
                    editing.moved = true;
                    editing.drawFrame();
                    editing.placeHandles();
                    // Wenn eine Ecke springt, muss zu sehen sein warum.
                    editing.showSnap?.(at, !!snapped);
                    /*
                     * Der Winkel steht in der Leiste, während gezogen wird.
                     *
                     * Er macht aus „schnappt nicht" eine Zahl: Wer sieht, dass 112° dasteht, weiß,
                     * dass er außerhalb des Keils ist, statt das Einrasten für kaputt zu halten.
                     * Auf ein Zehntel gerundet, damit nicht bei jedem Mausschritt neu gezeichnet
                     * wird.
                     */
                    const w = editing.working;
                    const angle = Math.round(
                        angleAt(w[(at - 1 + w.length) % w.length], w[at], w[(at + 1) % w.length]) * 10,
                    ) / 10;

                    setVertexUi((current) => (current.snapped === !!snapped && current.angle === angle
                        ? current
                        : { ...current, snapped: !!snapped, angle }));

                    return;
                }

                if (panZoomRef.current.dragAssetItem) {
                    const travelled = event.point.subtract(event.downPoint).length * (paper.view?.zoom || 1);

                    if (!panZoomRef.current.assetDragged && travelled < DRAG_THRESHOLD_PX) {
                        return;
                    }

                    panZoomRef.current.assetDragged = true;
                    panZoomRef.current.dragAssetItem.position = event.point;
                } else {
                    paper.view.center = paper.view.center.subtract(pan_offset);
                    rememberPan();
                    userAdjustedViewRef.current = true;
                    panZoomRef.current.panned = true;
                }
            }

            tool.onMouseUp = async (event: any) => {
                /*
                 * Der Ausstich.
                 *
                 * Beim Loslassen und nicht beim Drücken: Wer den Ausschnitt verschiebt, drückt
                 * ebenfalls auf den Plan, und ein Ausstich mitten im Schwenk wäre eine Handlung,
                 * die niemand gemeint hat. `panned` unterscheidet beides.
                 */
                if (stencilRef.current.active) {
                    if (panZoomRef.current.panned) {
                        return;
                    }

                    const edge = stencilRef.current.edge;
                    const offset = panZoomRef.current.imageOffset;
                    // Der Raster steht unskaliert, also ist eine Projekteinheit ein Bildpunkt;
                    // abgezogen wird nur, wo im Projekt seine linke obere Ecke liegt.
                    const cut: StencilCut = {
                        x: Math.round(event.point.x - offset.x - edge / 2),
                        y: Math.round(event.point.y - offset.y - edge / 2),
                        size: edge,
                        shape: stencilRef.current.shape,
                    };

                    const before = cutsRef.current;
                    const after = [...before, cut];

                    // Zwei Zustände der Liste, und der Schritt weiß den Weg zwischen ihnen. Mehr
                    // braucht der Verlauf nicht zu wissen — deshalb passt der Ausstich in denselben
                    // Stapel wie eine verschobene Ecke.
                    const applyCuts = (list: StencilCut[]) => {
                        stencilDirtyRef.current = true;
                        setCuts(list);
                    };

                    applyCuts(after);
                    rememberEdit({
                        label: t('ui.floorplanStencil'),
                        undo: () => applyCuts(before),
                        redo: () => applyCuts(after),
                    });

                    return;
                }

                if (panZoomRef.current.dragVertexIndex >= 0) {
                    const editing = vertexEditRef.current;
                    const vertex = panZoomRef.current.dragVertexIndex;

                    panZoomRef.current.dragVertexIndex = -1;

                    if (panZoomRef.current.vertexDragged) {
                        panZoomRef.current.vertexDragged = false;

                        if (editing) {
                            editing.moved = false;
                            editing.showSnap?.(vertex, false);
                            setVertexUi((current) => ({ ...current, snapped: false, angle: null }));
                            editing.persist();
                        }

                        return;
                    }

                    // Getippt, nicht gezogen: Das ist kein Formwechsel, sondern die Wahl der Ecke,
                    // die von hier an den Pfeiltasten gehorcht.
                    selectedVertexRef.current = selectedVertexRef.current === vertex ? -1 : vertex;
                    editing?.restyleHandles?.();
                    setVertexUi((current) => ({ ...current, selected: selectedVertexRef.current >= 0 }));

                    return;
                }

                if (panZoomRef.current.dragAssetItem && !panZoomRef.current.assetDragged) {
                    // Getippt, nicht gezogen: nichts speichern, sondern fragen, was gewollt ist.
                    const asset = panZoomRef.current.dragAssetItem.data?.asset;
                    const at = panZoomRef.current.pointerScreen;

                    panZoomRef.current.dragAssetItem = null;

                    if (asset && properties.assetClicked) {
                        properties.assetClicked(asset, at);
                    }

                    return;
                }

                /*
                 * Klick ins Leere hebt die Auswahl auf — die Ecke und den ganzen Raum.
                 *
                 * Nur wo **nichts** getroffen wurde: Wer einen anderen Raum anklickt, meint dessen
                 * Auswahl, und die setzt dessen eigener Klick. Ein Aufheben von hier aus käme ihm
                 * je nach Reihenfolge in die Quere.
                 *
                 * Und nur beim Klicken, nicht beim Ziehen: Den Ausschnitt zu verschieben ist keine
                 * Aussage über die Auswahl.
                 */
                // Über den Ref und nicht über `vertexEditIndex`: Das Werkzeug lebt im Aufbau der
                // Szene, und der kennt die Auswahl absichtlich nicht — sonst baute jede Auswahl
                // den Grundriss neu. Der Ref ist genau dann gesetzt, wenn ein Raum bearbeitet wird.
                if (!panZoomRef.current.dragAssetItem && vertexEditRef.current) {
                    /*
                     * Geschwenkt statt geklickt — daran, nicht an der Wegstrecke.
                     *
                     * Die Strecke in Projektkoordinaten ist beim Schwenken **null**: Der Ausschnitt
                     * folgt dem Zeiger, also bleibt der Punkt unter ihm stehen. Wer daran misst,
                     * hält jeden Schwenk für einen Klick und wirft die Auswahl weg.
                     */
                    /*
                     * Das Rasterbild zählt nicht als Treffer.
                     *
                     * Es liegt als Objekt über die ganze Bildfläche, also findet `hitTest` fast
                     * überall etwas — und „ins Leere geklickt" gäbe es dann nur außerhalb des
                     * Grundrisses. Gemeint ist: nichts, womit man hier umgeht.
                     */
                    const hit = paper.project.hitTest(event.point, hitOptions)?.item;

                    if (!panZoomRef.current.panned && (!hit || hit instanceof paper.Raster)) {
                        selectedVertexRef.current = -1;
                        setVertexUi({ selected: false, snapped: false, angle: null });
                        selectRoomVertices(-1);

                        return;
                    }
                }

                if (panZoomRef.current.dragAssetItem) {
                    const circle = panZoomRef.current.dragAssetItem.children['assetPosition'];

                    if (circle) {
                        const { x, y } = toBuildingCoordinates(circle.position, floor.floorplanImageScale, panZoomRef.current.imageOffset.x, panZoomRef.current.imageOffset.y);

                        panZoomRef.current.dragAssetItem.data.asset.x = x;
                        panZoomRef.current.dragAssetItem.data.asset.y = y;

                        const moved = panZoomRef.current.dragAssetItem.data.asset;

                        await userAPI.updateAsset(moved);

                        // Auch das Ziehen ist ein Verschieben: dieselbe Rückmeldung wie beim
                        // Setzen per Tipp, sonst hinge sie am Eingabegerät statt an der Handlung.
                        if (properties.placementSaved) {
                            properties.placementSaved(moved);
                        }
                    }

                    panZoomRef.current.dragAssetItem = null;
                }
            }
        }

        // Nach einem Neuaufbau (etwa nach ESC) hat paper das frische Projekt zwar aufgebaut,
        // aber nicht auf die Fläche gebracht — sie blieb leer, bis Zoom oder "Ansicht passend"
        // zufällig zeichneten. Einmal selbst zeichnen lassen (#735).
        requestAnimationFrame(() => {
            // Das Projekt kann zwischen Frame-Anforderung und Ausführung abgeräumt worden sein
            // (Tabwechsel, erneuter Aufbau). Ohne Projekt hat paper keinen Zeichenkontext, und
            // schon das Messen eines Textes wirft dann.
            if (cancelled || !paper.project || !paper.view) {
                return;
            }

            try {
                paper.view.update();
                // @ts-ignore
                paper.view.draw();
            } catch (error) {
                console.debug('Zeichnen nach Neuaufbau übersprungen', error);
            }
        });

        return cleanup;
        // updateSpace is intentionally accessed via updateSpaceRef, not listed
        // here, so a new function identity each render doesn't rebuild paper.js.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    // `spaces` steht hier bewusst **nicht**: Eine Formänderung soll die Räume erneuern, nicht den
    // Grundriss neu aufbauen. Das erledigt der Effekt darunter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [floor, assets, scaling, selectedSpaceIndex, floorplanImageLoaded, redrawTick, canvasSize]);

    /**
     * Geänderte Raumformen neu zeichnen — nur die Räume.
     *
     * Läuft nach dem Aufbau der Szene, weil er später steht; findet er noch keine Zeichenfunktion,
     * hat der Aufbau ohnehin gerade selbst gezeichnet.
     */
    useEffect(() => {
        drawRoomsRef.current?.(spaces);
        paper.view?.update?.();
    }, [spaces]);

    /**
     * Die Ausstiche als weiße Rechtecke über dem Rasterbild.
     *
     * Unmittelbar darüber und unter allem anderen: Ein Ausstich nimmt etwas aus dem **Plan**, nicht
     * aus den Räumen, die daraufgezeichnet sind. Läge er obenauf, verdeckte er die Raumfarbe, die
     * nach wie vor gilt.
     *
     * Läuft nach dem Aufbau der Szene, weil er später steht: Wo die Szene neu entsteht, sind diese
     * Rechtecke mit ihr fort und müssen wieder dazu — daher stehen dieselben Abhängigkeiten hier
     * noch einmal.
     */
    useEffect(() => {
        const raster = rasterRef.current;

        if (!paper.project || !paper.view || !raster?.project || cuts.length === 0) {
            return;
        }

        const offset = panZoomRef.current.imageOffset;
        const items = cuts.map((cut) => {
            const item = stencilPath(
                cut.shape,
                new Point(offset.x + cut.x + cut.size / 2, offset.y + cut.y + cut.size / 2),
                cut.size,
            );

            item.fillColor = new paper.Color('#ffffff');
            item.insertAbove(raster);

            return item;
        });

        // Und die Räume darüber neu malen.
        //
        // Der Ausstich nimmt etwas aus dem **Plan**; was an Räumen erfasst ist, gilt weiter und
        // gehört obenauf. Das Einhängen über dem Rasterbild allein verlässt sich darauf, wo die
        // Raumpfade gerade in der Reihenfolge stehen — nach einem Neuaufbau der Szene ist das nicht
        // dasselbe. Einmal neu malen ist billig und lässt keinen Zweifel.
        drawRoomsRef.current?.(spacesRef.current);

        paper.view.update?.();

        return () => {
            // Wie überall hier: nur entfernen, solange es ein Projekt gibt, aus dem zu entfernen
            // wäre — der Aufbau der Szene räumt vor uns auf.
            try {
                if (paper.view) {
                    items.forEach((item) => item.remove());
                }
            } catch {
                // Das Projekt war schon fort. Dann sind es die Flächen mit ihm.
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cuts, floor, floorplanImageLoaded, redrawTick, canvasSize]);

    /**
     * Die Vorschaufläche, solange der Stempel geführt wird.
     *
     * Es liegt über allem — es zeigt an, was gleich fortfällt, und darf deshalb von nichts verdeckt
     * werden. Der Zeiger schiebt es im Werkzeug; hier entsteht und vergeht es nur.
     */
    useEffect(() => {
        stencilPreviewRef.current = null;

        if (!stencilRunning || !paper.project || !paper.view) {
            return;
        }

        const preview = stencilPath(stencilShape, paper.view.center, stencilEdge);

        preview.fillColor = new paper.Color('#ffffff');
        preview.fillColor.alpha = 0.6;
        preview.strokeColor = new paper.Color('#e11d48');
        preview.strokeScaling = false;
        preview.strokeWidth = 1.5;
        preview.dashArray = [4, 3];
        // Sonst nimmt die Fläche die Klicks entgegen, die dem Plan darunter gelten.
        preview.locked = true;
        preview.position = paper.view.center;

        stencilPreviewRef.current = preview;
        paper.view.update?.();

        return () => {
            stencilPreviewRef.current = null;

            try {
                if (paper.view) {
                    preview.remove();
                }
            } catch {
                // Das Projekt war schon fort.
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stencilRunning, stencilEdge, stencilShape, floor, floorplanImageLoaded, redrawTick, canvasSize]);

    /**
     * Das Bild, wie es beim Öffnen der Seite war — die Grundlage jedes Ausstichs.
     *
     * Zusammengesetzt wird **nicht** aus dem zuletzt Hochgeladenen, sondern immer aus diesem Bild
     * und der ganzen Liste. Nur so wirkt ein Rückgängig auch auf das Gespeicherte: Rechnete man
     * jeden Ausstich in das vorige Ergebnis, wäre er dort eingebrannt und nicht mehr zu lösen.
     */
    const stencilBaseRef = useRef<{ uri: string; bitmap: any } | null>(null);

    /** Die Liste, wie sie gerade ist — für Handler, die keinen Zustand aus dem Rendern sehen. */
    const cutsRef = useRef<StencilCut[]>([]);

    cutsRef.current = cuts;

    /** Erst eine Handlung macht das Hochladen fällig; ein bloßes Aufbauen nicht. */
    const stencilDirtyRef = useRef(false);
    const stencilSaveTimerRef = useRef<any>(null);

    /**
     * Das Ergebnis ablegen: ursprüngliches Bild, alle Ausstiche weiß darüber, hochladen.
     *
     * Über `fetch` und nicht über das `<img>` im Seitenbaum: Das Bild kommt von einem anderen
     * Ursprung als die Seite, und ein daraus gezeichnetes Canvas wäre „getaint" — `toBlob` würfe.
     * Die Bytes selbst zu holen umgeht das; der Server erlaubt die Herkunft ohnehin.
     */
    const saveStencilRef = useRef<() => Promise<void>>(async () => {});

    saveStencilRef.current = async () => {
        const uri = floor?.floorplanImageURI;

        if (!uri) {
            return;
        }

        try {
            setStencilSaving(true);

            if (stencilBaseRef.current?.uri !== uri) {
                const response = await fetch(userAPI.getResourceURL(uri), { credentials: 'include' });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                stencilBaseRef.current = { uri, bitmap: await createImageBitmap(await response.blob()) };
            }

            const base = stencilBaseRef.current?.bitmap;

            if (!base) {
                throw new Error('Kein Ausgangsbild');
            }

            const canvas = document.createElement('canvas');

            canvas.width = base.width;
            canvas.height = base.height;

            const context = canvas.getContext('2d');

            if (!context) {
                throw new Error('Kein 2D-Kontext');
            }

            context.drawImage(base, 0, 0);
            context.fillStyle = '#ffffff';
            // Dieselben Formen wie auf dem Schirm — sonst zeigte die Vorschau etwas anderes, als
            // gespeichert wird.
            cutsRef.current.forEach((cut) => {
                if (cut.shape === 'circle') {
                    context.beginPath();
                    context.arc(cut.x + cut.size / 2, cut.y + cut.size / 2, cut.size / 2, 0, Math.PI * 2);
                    context.fill();

                    return;
                }

                context.fillRect(cut.x, cut.y, cut.size, cut.size);
            });

            const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));

            if (!blob) {
                throw new Error('Kein Bild');
            }

            const form = new FormData();

            form.append('floorplanImage', blob, 'floorplanImage.png');
            form.append('width', `${canvas.width}`);
            form.append('height', `${canvas.height}`);

            await updateFloorplanImage(properties.floor, form);
        } catch (error) {
            console.error('Ausstechen konnte nicht gespeichert werden', error);
            setVertexHint(t('ui.floorplanStencilSaveFailed'));
        } finally {
            setStencilSaving(false);
        }
    };

    /**
     * Gebündelt ablegen, kurz nach dem letzten Ausstich.
     *
     * Wer eine Ecke des Plans freiräumt, sticht fünf-, sechsmal — und das wären fünf, sechs
     * vollständige Bilder über die Leitung. Ein Ausstich ist keine Zäsur; die Reihe ist eine.
     */
    useEffect(() => {
        if (!stencilDirtyRef.current) {
            return;
        }

        if (stencilSaveTimerRef.current) {
            clearTimeout(stencilSaveTimerRef.current);
        }

        stencilSaveTimerRef.current = setTimeout(() => {
            stencilSaveTimerRef.current = null;
            void saveStencilRef.current();
        }, 1200);
    }, [cuts]);

    /** Beim Verlassen geht heraus, was noch aussteht — sonst wäre es stillschweigend verloren. */
    useEffect(() => () => {
        if (stencilSaveTimerRef.current) {
            clearTimeout(stencilSaveTimerRef.current);
            stencilSaveTimerRef.current = null;
            void saveStencilRef.current();
        }
    }, []);

    /**
     * Ein neuer Plan setzt den Verlauf zurück.
     *
     * Die Ausstiche sind Punkte **dieses** Bildes. Käme ein anderes, träfen sie irgendwo — und ein
     * Verlauf, in dem die Hälfte der Schritte auf einen Plan zeigt, den es nicht mehr gibt, wäre
     * schlimmer als keiner: Man drückte Rückgängig und bekäme etwas anderes als das Erwartete.
     */
    useEffect(() => {
        stencilDirtyRef.current = false;
        stencilBaseRef.current = null;
        setCuts([]);
        setHistory(EMPTY_HISTORY);
    }, [floor?.floorplanImageURI]);

    /**
     * Rahmen und Eckgriffe des ausgewählten Raums.
     *
     * Ein eigener Effekt, der nur Objekte hinzufügt — **nicht** die Auswahl in den Aufbau der
     * Szene hängen. Der baut den Grundriss samt Rasterbild neu, und dessen Nachladen ist auf drei
     * erzwungene Anläufe begrenzt (`forcedRedrawsRef`); ein paar Klicks auf Räume verbrauchten das
     * Budget, und danach blieb die Zeichenfläche leer. Eine Auswahl ist kein Grund, ein Bild neu
     * zu laden.
     *
     * Läuft nach dem Aufbau, weil er später steht: React führt die Effekte in der Reihenfolge ihrer
     * Deklaration aus. Deshalb stehen dieselben Abhängigkeiten hier noch einmal — wo die Szene neu
     * entsteht, sind die Objekte von hier mit ihr verschwunden und müssen wieder dazu.
     */
    useEffect(() => {
        // Die Griffe sind eigene Kreise und nicht die Punkte, die paper an einem `selected` Pfad
        // zeichnet: Jene sind Dekoration, nicht anfassbar, und ihre Größe gehört paper.
        const VERTEX_HANDLE_RADIUS = 5;
        const SELECTION_BLUE = '#009dec';

        vertexEditRef.current = null;

        if (vertexEditIndex < 0 || vertexEditIndex === selectedSpaceIndex || !spaces?.[vertexEditIndex]) {
            return;
        }

        if (!paper.project || !paper.view) {
            return;
        }

        const space: any = spaces[vertexEditIndex];
        const stored = toPointList(space.shape);

        if (stored.length < 3) {
            return;
        }

        // Die Auswahl überlebt einen Neuaufbau desselben Raums, nicht aber den Wechsel zu einem
        // anderen — dort meinte sie eine Ecke, die es hier nicht gibt.
        if (vertexSelectionRoomRef.current !== vertexEditIndex) {
            selectedVertexRef.current = -1;
            vertexSelectionRoomRef.current = vertexEditIndex;
        }

        if (selectedVertexRef.current >= stored.length) {
            selectedVertexRef.current = -1;
        }

        const offset = panZoomRef.current.imageOffset;
        // Gearbeitet wird in Projektkoordinaten; abgelegt wird in Gebäudekoordinaten.
        let working = stored.map((point: any) =>
            toProjectCoordinates(point, floor.floorplanImageScale, offset.x, offset.y));

        // Der Radius steht in Bildschirmpixeln — mal 1/zoom, damit ein Griff bei jedem Zoom gleich
        // groß bleibt. In Projekteinheiten gemessen wäre er eingepasst ein Punkt und herangezoomt
        // eine Scheibe.
        const markerScale = paper.view.zoom > 0 ? 1 / paper.view.zoom : 1;

        const frame = new Path();

        frame.strokeColor = new paper.Color(SELECTION_BLUE);
        frame.strokeScaling = false;
        frame.strokeWidth = 2;

        /**
         * Dieselbe Linie noch einmal, dick und unsichtbar — die Trefferfläche für den Doppelklick.
         *
         * Der sichtbare Rahmen ist zwei Pixel breit, und paper prüft den Treffer ohne Toleranz. Auf
         * einer **geteilten Wand** liegt schon einen Pixel daneben der Nachbarraum, und der Klick
         * ging an ihn: Deshalb ließ sich auf den senkrechten Außenwänden eine Ecke einfügen, auf
         * den waagerechten zwischen zwei Räumen aber nicht.
         *
         * Fast unsichtbar statt unsichtbar: Bei `opacity = 0` überspringt paper das Objekt beim
         * Treffertest, und der Klick fiele wieder auf den Raum darunter durch — genau der Fehler,
         * den diese Fläche beheben soll. Ein Hundertstel ist nicht zu sehen und zählt.
         */
        const hitArea = new Path();

        hitArea.strokeColor = new paper.Color(SELECTION_BLUE);
        hitArea.strokeScaling = false;
        hitArea.strokeWidth = 14;
        hitArea.opacity = 0.01;

        const handles: any[] = [];
        /** Das Winkelzeichen, solange eingerastet ist. */
        let snapMarker: any = null;

        const drawFrame = () => {
            frame.removeSegments();
            hitArea.removeSegments();

            working.forEach((point: any) => {
                frame.add(new Point(point.x, point.y));
                hitArea.add(new Point(point.x, point.y));
            });

            frame.closed = true;
            hitArea.closed = true;
        };

        const placeHandles = () => {
            handles.forEach((handle, at) => {
                handle.position = new Point(working[at].x, working[at].y);
            });
        };

        /**
         * Die gewählte Ecke ausgefüllt, die übrigen hohl.
         *
         * Ohne diesen Unterschied wüsste niemand, welche Ecke die Pfeiltasten bewegen — und die
         * Tasten wirkten wie Zufall.
         */
        /**
         * Zeigt an, dass die Ecke eingerastet ist — grün statt blau, solange sie es ist.
         *
         * Ohne diese Rückmeldung sieht ein Einschnappen aus wie ein Fehler: Die Ecke springt, und
         * niemand weiß warum.
         */
        const showSnap = (at: number, snapped: boolean) => {
            const handle = handles[at];

            if (handle) {
                handle.strokeColor = new paper.Color(snapped ? SNAP_GREEN : SELECTION_BLUE);
                handle.strokeWidth = snapped ? 3 : 2;
            }

            snapMarker?.remove();
            snapMarker = null;

            if (!snapped) {
                return;
            }

            // Bogen mit Punkt — die Darstellung des rechten Winkels im deutschen technischen
            // Zeichnen. Der Bogen allein hieße nur „hier ist ein Winkel"; erst der Punkt sagt,
            // welcher. Das kleine Quadrat wäre die angelsächsische Schreibweise.
            const at_ = working[at];
            const prev = working[(at - 1 + working.length) % working.length];
            const next = working[(at + 1) % working.length];
            const size = RIGHT_ANGLE_MARKER_PX / (paper.view?.zoom || 1);
            const corners = rightAngleMarker(prev, at_, next, size);

            if (!corners) {
                return;
            }

            // Bogen und Punkt zusammen, damit sie gemeinsam kommen und gehen.
            snapMarker = new paper.Group();

            const arc = new Path.Arc(
                new Point(corners.from.x, corners.from.y),
                new Point(corners.through.x, corners.through.y),
                new Point(corners.to.x, corners.to.y),
            );

            arc.strokeColor = new paper.Color(SNAP_GREEN);
            arc.strokeScaling = false;
            arc.strokeWidth = 2;

            const dot = new Path.Circle(new Point(corners.dot.x, corners.dot.y), size * 0.14);

            dot.fillColor = new paper.Color(SNAP_GREEN);

            snapMarker.addChild(arc);
            snapMarker.addChild(dot);
        };

        const restyleHandles = () => {
            handles.forEach((handle, at) => {
                handle.fillColor = at === selectedVertexRef.current
                    ? new paper.Color(SELECTION_BLUE)
                    : new paper.Color('#ffffff');
            });

            paper.view?.update?.();
        };

        /** Die neue Form ablegen — derselbe Weg, den auch das Neuzeichnen eines Raums nimmt. */
        const persist = () => {
            const buildingPoints: any = working.map((point: any) =>
                toBuildingCoordinates(point, floor.floorplanImageScale, offset.x, offset.y));
            // Wie der Raum **jetzt** abgelegt ist, nicht wie er beim Aufbau dieses Effekts stand:
            // Zwischen zwei Speicherungen kann eine dritte liegen, und ein Rückgängig spränge sonst
            // weiter zurück als der eine Zug, den es meint.
            const previousSpace = spacesRef.current?.[vertexEditIndex] ?? space;
            const newSpace = {
                ...space,
                area: getConvexPolygonArea(buildingPoints) / 1000000,
                shape: buildingPoints,
            };

            // Der Schritt kennt nur zwei Formen desselben Raums. Zurücklegen heißt speichern und
            // den Zustand setzen; das Neuzeichnen hängt am Zustand und läuft von selbst.
            rememberEdit({
                label: t('ui.floorplanRoomShapeChanged'),
                undo: () => applySpaceRef.current(previousSpace),
                redo: () => applySpaceRef.current(newSpace),
            });

            const thisSequence = ++persistSequenceRef.current;

            updateSpaceRef.current(newSpace).then(() => {
                // Nur anwenden, wenn keine neuere Speicherung unterwegs ist
                if (thisSequence !== persistSequenceRef.current) {
                    return;
                }

                setSpaces((current: any[]) => {
                    const next = [...(current ?? [])];

                    next[vertexEditIndex] = newSpace;

                    return next;
                });

                if (properties.spacesChanged) {
                    properties.spacesChanged();
                }

                /*
                 * Nicht aus einem `setSpaces`-Updater heraus melden.
                 *
                 * Updater müssen frei von Nebenwirkungen sein: React ruft sie in der Entwicklung
                 * zweimal auf, und der Rückruf landete damit doppelt — und mitten im Rendern einer
                 * fremden Komponente. Die neue Liste lässt sich hier ohnehin bilden; `spacesRef`
                 * trägt den aktuellen Stand.
                 */
                if (properties.floorChanged) {
                    const next = [...(spacesRef.current ?? [])];

                    next[vertexEditIndex] = newSpace;

                    properties.floorChanged({ ...properties.floor, spaces: next });
                }
            });
        };

        const buildHandle = (point: any, at: number) => {
            const handle = new Path.Circle(new Point(point.x, point.y), VERTEX_HANDLE_RADIUS * markerScale);

            handle.strokeColor = new paper.Color(SELECTION_BLUE);
            handle.strokeScaling = false;
            handle.strokeWidth = 2;
            // Woran das Werkzeug einen Griff erkennt, wenn die Maus auf ihm heruntergeht.
            handle.data = { vertexHandle: true, vertexIndex: at };

            /** Doppelklick auf den Griff: Ecke weg — solange mehr als drei übrig bleiben. */
            handle.onDoubleClick = () => {
                if (!canRemoveVertex(working)) {
                    // Kein stilles Nichts: Wer doppelklickt, erwartet eine Wirkung, und „geht
                    // nicht" ist eine Auskunft.
                    setVertexHint(t('ui.floorplanMinimumVertices'));

                    return false;
                }

                const currentIndex = handle.data.vertexIndex;
                working = removeVertex(working, currentIndex);
                // Synchronize handles array: remove the handle and update indices
                const removed = handles.splice(currentIndex, 1)[0];
                removed?.remove();
                handles.forEach((h, i) => {
                    if (h.data) {
                        h.data.vertexIndex = i;
                    }
                });
                drawFrame();
                placeHandles();
                persist();

                return false;
            };

            return handle;
        };

        drawFrame();
        working.forEach((point: any, at: number) => handles.push(buildHandle(point, at)));

        /** Doppelklick auf eine Kante: neue Ecke genau auf ihr, dort wo geklickt wurde. */
        /*
         * Die Fläche muss auch den einfachen Klick annehmen.
         *
         * paper prüft beim Treffertest nur Objekte, die für **diesen** Ereignistyp einen Handler
         * tragen. Mit nur `doubleclick` wurde sie beim Klicken übersprungen, der Raum darunter bekam
         * ihn, wählte sich ab — und der Doppelklick danach fand weder Rahmen noch Fläche vor.
         *
         * Ohne `return false`: Das hielte in paper die ganze Kette an und nähme der Fläche ihren
         * eigenen Doppelklick. Es genügt, Ziel zu sein.
         */
        hitArea.onClick = () => {};

        hitArea.onDoubleClick = (event: any) => {
            const hit = nearestEdge(working, { x: event.point.x, y: event.point.y });

            if (!hit) {
                return false;
            }

            working = insertVertexAfter(working, hit.edgeIndex, hit.at);
            // Synchronize handles array: insert new handle and update indices
            const newIndex = hit.edgeIndex + 1;
            const newHandle = buildHandle(working[newIndex], newIndex);
            handles.splice(newIndex, 0, newHandle);
            handles.forEach((h, i) => {
                if (h.data) {
                    h.data.vertexIndex = i;
                }
            });
            drawFrame();
            placeHandles();
            persist();

            return false;
        };

        restyleHandles();
        setVertexUi((current) => (current.selected === (selectedVertexRef.current >= 0)
            ? current
            : { ...current, selected: selectedVertexRef.current >= 0 }));

        /**
         * Die Ecke mit den Pfeiltasten schieben — genauer, als eine Maus je sein kann.
         *
         * Abgelegt wird nicht bei jedem Tastendruck: Wer eine Kante um zehn Zentimeter rückt,
         * drückt zehnmal, und das wären zehn Schreibvorgänge für eine Handlung. Stattdessen kurz
         * nach dem letzten Druck — und sofort, wenn die Auswahl endet.
         */
        let saveTimer: any = null;

        const scheduleSave = () => {
            if (saveTimer) {
                clearTimeout(saveTimer);
            }

            saveTimer = setTimeout(() => {
                saveTimer = null;
                persist();
            }, 700);
        };

        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null;

            // Eine Pfeiltaste in einem Eingabefeld gehört dem Feld.
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                return;
            }

            if (selectedVertexRef.current < 0) {
                return;
            }

            if (event.key === 'Escape') {
                selectedVertexRef.current = -1;
                restyleHandles();
                setVertexUi((current) => ({ ...current, selected: false }));

                return;
            }

            const nudge = nudgeFor(event.key, event);

            if (!nudge) {
                return;
            }

            // Sonst rollt die Seite unter dem Plan weg.
            event.preventDefault();

            // Der Versatz kommt in Millimetern; gezeichnet wird in Projekteinheiten.
            const perMillimetre = floor.floorplanImageScale / 1000;

            working = moveVertex(working, selectedVertexRef.current, {
                x: working[selectedVertexRef.current].x + nudge.x * perMillimetre,
                y: working[selectedVertexRef.current].y + nudge.y * perMillimetre,
            });

            drawFrame();
            placeHandles();
            paper.view?.update?.();
            scheduleSave();
        };

        document.addEventListener('keydown', onKeyDown);

        vertexEditRef.current = {
            spaceIndex: vertexEditIndex,
            // Der Rahmen trägt die Ausdehnung des Raums — daran hängt das Papierkorbzeichen.
            frame,
            restyleHandles,
            showSnap,
            dropSnapMarker: () => {
                snapMarker?.remove();
                snapMarker = null;
            },
            // Erst ein wirklicher Zug macht daraus einen Formwechsel.
            moved: false,
            get working() { return working; },
            set working(next: any) { working = next; },
            handles,
            drawFrame,
            placeHandles,
            persist,
        };

        paper.view.update?.();

        return () => {
            document.removeEventListener('keydown', onKeyDown);

            // Was noch aussteht, geht jetzt raus: Die Auswahl endet, und eine verworfene
            // Verschiebung wäre eine Handlung, die stillschweigend nicht stattgefunden hat.
            if (saveTimer) {
                clearTimeout(saveTimer);
                persist();
            }

            // `selectedVertexRef` bleibt: Der Effekt läuft auch nach jedem Speichern neu, und die
            // Auswahl gehört zur Handlung, nicht zum Aufbau. Beim Wechsel des Raums wird sie oben
            // verworfen.
            vertexEditRef.current = null;
            setVertexUi((current) => ({ ...current, snapped: false, angle: null }));

            /*
             * Nur entfernen, solange es ein Projekt gibt, aus dem zu entfernen wäre.
             *
             * React räumt in der Reihenfolge der Deklaration auf, und der Aufbau der Szene steht
             * vorher — der hat `paper.project.remove()` schon gerufen. Ein `remove()` danach greift
             * in paper auf die View des Elternprojekts zu, und die ist fort: „Cannot read
             * properties of null (reading '_countItemEvent')", mitten im Unmount und damit als
             * roter Schirm.
             */
            try {
                if (paper.view && frame.project) {
                    snapMarker?.remove();
                    hitArea.remove();
                    frame.remove();
                    handles.forEach((handle) => handle.remove());
                }
            } catch {
                // Das Projekt war schon fort. Dann sind es die Objekte mit ihm.
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vertexEditIndex, selectedSpaceIndex, spaces, floor, floorplanImageLoaded, redrawTick, canvasSize]);

    /**
     * Das Papierkorbzeichen am ausgewählten Raum halten.
     *
     * Es hängt an der oberen rechten Ecke des Auswahlrahmens, und die wandert bei jedem
     * Verschieben, Zoomen und bei jedem Zug an einer Ecke. Es gibt in paper kein Ereignis, das
     * eine geänderte Ansicht meldet, und die Wege dorthin sind viele — Rad, Zwei-Finger-Geste,
     * die Schaltflächen der Leiste, das Ziehen mit der Maus. Statt an jedem einzelnen zu hängen,
     * wird nachgesehen, solange ein Raum ausgewählt ist: einmal je Bild, und der Zustand ändert
     * sich nur, wenn die Stelle es tut.
     */
    useEffect(() => {
        if (vertexEditIndex < 0 || !properties.spaceDeleteRequested || properties.readonly) {
            setDeleteAnchor(null);

            return;
        }

        let frameRequest = 0;

        const follow = () => {
            frameRequest = requestAnimationFrame(follow);

            const editing = vertexEditRef.current;

            if (!editing?.frame || !paper.view || !editing.frame.project) {
                return;
            }

            const bounds = editing.frame.bounds;
            const corner = paper.view.projectToView(new Point(bounds.right, bounds.top));

            setDeleteAnchor((current) => (current
                && Math.abs(current.x - corner.x) < 0.5
                && Math.abs(current.y - corner.y) < 0.5
                ? current
                : { x: corner.x, y: corner.y }));
        };

        frameRequest = requestAnimationFrame(follow);

        return () => {
            cancelAnimationFrame(frameRequest);
            setDeleteAnchor(null);
        };
    }, [vertexEditIndex, properties.spaceDeleteRequested, properties.readonly]);

    // Prevent wheel even bubble up

    const onWheel = useCallback((e: any) => {
        e.preventDefault();
    }, []);

    const divRefCallback = useCallback((node: any) => {
        if (node == null) {
            return;
        }

        node.addEventListener('wheel', onWheel, { passive: false });
    }, [onWheel]);

    const canvasRefCallback = useCallback((node: any) => {
        canvasRef.current = node;
        setCanvasNode(node);
        // canvasRef is a ref, so it is stable and not a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The drop target and the size measurement share one element.
    const containerRefCallback = useCallback((node: any) => {
        drop(node);
        setCanvasContainer(node);
    }, [drop]);

    return <div>
        {floor && floor.floorplanImageURI && floor.floorplanImageWidth && floor.floorplanImageHeight
            ?
            <div className="border border-border bg-white" style={{ cursor: selectedSpaceIndex >= 0 || scaling ? 'crosshair' : 'pointer' }}>
                <div className="flex flex-wrap items-center gap-2 p-1">
                    {!properties.readonly &&
                        <ImageUploading
                            value={images}
                            onChange={onChange}
                            maxNumber={1}
                            maxFileSize={8000000}
                            dataURLKey="data_url"
                        >
                            {({
                                imageList: _imageList,
                                onImageUpload,
                                onImageRemoveAll: _onImageRemoveAll,
                                onImageUpdate: _onImageUpdate,
                                onImageRemove: _onImageRemove,
                                isDragging: _isDragging,
                                dragProps: _dragProps,
                            }) => (
                                <Button type="button" onClick={onImageUpload}>
                                    {t('ui.floorplanReplace')}
                                </Button>
                            )}
                        </ImageUploading>}
                    <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2 text-primary'>
                        {/*
                          * Was gerade zu tun ist, in der Leiste — dort ist Platz, und dort sucht
                          * man es. Die Reihenfolge ist die der Dringlichkeit: eine Absage zuerst,
                          * dann der laufende Modus, dann was die Auswahl erlaubt.
                          */}
                        <span
                            className={cn(
                                'min-w-0 flex-1 basis-full truncate text-sm sm:basis-auto',
                                vertexHint && 'font-medium text-destructive',
                                !vertexHint && vertexUi.snapped && 'font-medium text-success',
                            )}
                        >
                            {vertexHint
                                ? vertexHint
                                : stencilRunning
                                    ? t('ui.floorplanStencilHint')
                                : vertexUi.angle !== null
                                    ? `${t('ui.floorplanAngle')}: ${scaleFormat.format(vertexUi.angle)}°${vertexUi.snapped ? ` — ${t('ui.floorplanSnapped')}` : ''}`
                                : selectedSpaceIndex >= 0
                                    ? t('ui.floorplanDrawHint')
                                    : scaling
                                        ? t('ui.floorplanScaleHint')
                                        : properties.placementAsset
                                            ? t('ui.floorplanPlaceHint')
                                            : vertexUi.snapped
                                                ? t('ui.floorplanSnapped')
                                                : vertexUi.selected
                                                    ? t('ui.floorplanVertexSelected')
                                                    : vertexEditIndex >= 0
                                                        ? t('ui.floorplanRoomSelected')
                                                        : ''}
                        </span>
                        {/* Auf einem Tablet gibt es weder ENTER noch ESC — ohne diese beiden
                            Schaltflächen käme dort niemand aus dem Zeichenmodus heraus. */}
                        {selectedSpaceIndex >= 0 &&
                            <div className="flex shrink-0 items-center gap-2">
                                <Button type="button" size="sm" disabled={drawnPointCount < 3}
                                    onClick={() => drawingActionsRef.current?.finish()}>{t('ui.floorplanDone')}</Button>
                                <Button type="button" size="sm" variant="outline"
                                    onClick={() => drawingActionsRef.current?.cancel()}>{t('ui.floorplanCancel')}</Button>
                            </div>}
                        {properties.placementAsset && properties.placementFinished &&
                            <Button type="button" size="sm" variant="outline" className="shrink-0"
                                onClick={() => properties.placementFinished?.()}>{t('ui.floorplanCancelPlacement')}</Button>}
                        {/*
                          * Die Bedienung des Stempels: erst die Größe, dann der Verlauf, dann der
                          * Ausgang. Sie steht nur da, solange ausgestochen wird — ein Werkzeug, das
                          * niemand führt, braucht keine Leiste.
                          */}
                        {/* Was noch nicht abgelegt ist, soll man sehen — sonst schließt man die
                            Seite im Glauben, es sei durch. */}
                        {stencilSaving && !properties.readonly &&
                            <span className="shrink-0 text-xs text-muted-foreground">
                                {t('ui.floorplanStencilSaving')}
                            </span>}
                    </div>
                    {!properties.readonly &&
                        <div className="whitespace-nowrap"><span className="font-bold">{t('ui.floorplanScale')}: </span>{scaleFormat.format(floor.floorplanImageScale)} px/m</div>}
                    {/* Zoom controls are always available: a touch device has no wheel,
                        and a pinch is not discoverable. */}
                    <div className="flex items-center">
                        <Button type="button" variant="ghost" size="icon" aria-label={t('ui.floorplanZoomOut')}
                            onClick={() => applyZoom(paper.view ? paper.view.zoom * 0.8 : 0)}><ZoomOut aria-hidden="true" className="h-4 w-4" /></Button>
                        <Button type="button" variant="ghost" size="icon" aria-label={t('ui.floorplanZoomIn')}
                            onClick={() => applyZoom(paper.view ? paper.view.zoom * 1.25 : 0)}><ZoomIn aria-hidden="true" className="h-4 w-4" /></Button>
                        <Button type="button" variant="ghost" size="icon" aria-label={t('ui.floorplanFit')}
                            onClick={fitToWindow}><Maximize2 aria-hidden="true" className="h-4 w-4" /></Button>
                        {!properties.readonly &&
                            <Button type="button" variant="ghost" size="icon" aria-label="Grundriss skalieren"
                                onClick={async () => setScaling(true)}><Ruler aria-hidden="true" className="h-4 w-4" /></Button>}
                        {/*
                          * Rückgängig und Wiederherstellen gehören der ganzen Leiste, nicht einem
                          * Werkzeug: Sie stehen immer da, und ihr Kurztext nennt, was drankäme —
                          * „Rückgängig: Ausgestochen" sagt vor dem Klick, worauf man sich einlässt.
                          */}
                        {!properties.readonly && <>
                            <Button type="button" variant="ghost" size="icon"
                                aria-label={undoLabel(history)
                                    ? `${t('ui.floorplanUndo')}: ${undoLabel(history)}`
                                    : t('ui.floorplanUndo')}
                                title={undoLabel(history)
                                    ? `${t('ui.floorplanUndo')}: ${undoLabel(history)}`
                                    : t('ui.floorplanUndo')}
                                disabled={!canUndo(history)} onClick={runUndo}>
                                <Undo2 aria-hidden="true" className="h-4 w-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="icon"
                                aria-label={redoLabel(history)
                                    ? `${t('ui.floorplanRedo')}: ${redoLabel(history)}`
                                    : t('ui.floorplanRedo')}
                                title={redoLabel(history)
                                    ? `${t('ui.floorplanRedo')}: ${redoLabel(history)}`
                                    : t('ui.floorplanRedo')}
                                disabled={!canRedo(history)} onClick={runRedo}>
                                <Redo2 aria-hidden="true" className="h-4 w-4" />
                            </Button>
                        </>}
                        {/*
                          * Der Stempel, und an ihm seine Größen.
                          *
                          * Am Zeichen und nicht in der Leiste: Drei Größen sind Zubehör eines
                          * Werkzeugs, nicht drei eigene Werkzeuge, und in der Leiste stünden sie
                          * gleichrangig neben Zoom und Lineal. Beim Einschalten fällt eine laufende
                          * Raumauswahl weg — zwei Werkzeuge auf demselben Klick wären eine Wette.
                          *
                          * Das Zeichen ist ein Schalter: Es aufzugeben schaltet den Stempel aus.
                          * Ein eigenes „Fertig" daneben wäre ein zweiter Weg für dieselbe Sache —
                          * und der Weg heraus soll dort sein, wo der Weg hinein war. Deshalb öffnet
                          * ein Klick die Größen nur, solange nicht ausgestochen wird.
                          */}
                        {!properties.readonly &&
                            <Popover
                                open={stencilMenuOpen}
                                onOpenChange={(next) => {
                                    if (stencilActive) {
                                        setStencilActive(false);
                                        setStencilMenuOpen(false);

                                        return;
                                    }

                                    setStencilMenuOpen(next);
                                }}
                            >
                                <PopoverTrigger asChild>
                                    <Button type="button" variant={stencilActive ? 'default' : 'ghost'} size="icon"
                                        aria-pressed={stencilActive}
                                        aria-label={stencilActive ? t('ui.floorplanStencilStop') : t('ui.floorplanStencil')}
                                        title={stencilActive ? t('ui.floorplanStencilStop') : t('ui.floorplanStencil')}>
                                        <Eraser aria-hidden="true" className="h-4 w-4" />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-auto">
                                    {/*
                                      * Acht Stempel: vier eckige, vier runde, jeder in seiner
                                      * eigenen Größe gezeichnet.
                                      *
                                      * Das Zeichen **ist** die Auskunft — es zeigt Form und Größe
                                      * zugleich, und zwar im Verhältnis zueinander. Eine Zahl
                                      * daneben ließe sich lesen, aber nicht vergleichen: „0,3 m"
                                      * sagt niemandem, ob das die Bemaßungszahl trifft oder das
                                      * halbe Zimmer. Das Maß steht im Kurztext, für den, der es
                                      * genau wissen will.
                                      */}
                                    <div className="flex flex-col gap-2">
                                        <span className="text-sm font-medium">{t('ui.floorplanStencilSize')}</span>
                                        {(['square', 'circle'] as StencilShape[]).map((shape) => (
                                            <div key={shape} className="flex items-end gap-1">
                                                {STENCIL_SIZES_METRES.map((metres, step) => {
                                                    const chosen = stencilActive
                                                        && shape === stencilShape
                                                        && metres === stencilMetres;
                                                    // Vier Stufen von 8 bis 26 Pixeln — die Zeichen
                                                    // wachsen sichtbar, ohne das Menü zu sprengen.
                                                    const drawn = 8 + step * 6;

                                                    return (
                                                        <Button
                                                            key={metres}
                                                            type="button"
                                                            variant={chosen ? 'default' : 'outline'}
                                                            aria-pressed={chosen}
                                                            aria-label={`${shape === 'circle'
                                                                ? t('ui.floorplanStencilCircle')
                                                                : t('ui.floorplanStencilSquare')} ${stencilSizeFormat.format(metres)} m`}
                                                            title={`${shape === 'circle'
                                                                ? t('ui.floorplanStencilCircle')
                                                                : t('ui.floorplanStencilSquare')} · ${stencilSizeFormat.format(metres)} m`}
                                                            className="h-11 w-11 p-0"
                                                            onClick={() => {
                                                                // Einen Stempel zu wählen heißt, ihn
                                                                // zu wollen — sonst wäre die Wahl
                                                                // folgenlos. Und wer ihn will, will
                                                                // nicht mehr zeichnen oder messen.
                                                                selectRoomVertices(-1);
                                                                setSelectedSpaceIndex(-1);
                                                                setScaling(false);
                                                                setStencilShape(shape);
                                                                setStencilMetres(metres);
                                                                setStencilActive(true);
                                                                setStencilMenuOpen(false);
                                                            }}
                                                        >
                                                            <span
                                                                aria-hidden="true"
                                                                className={cn(
                                                                    'block border-2 border-current',
                                                                    shape === 'circle' ? 'rounded-full' : 'rounded-[1px]',
                                                                )}
                                                                style={{ width: drawn, height: drawn }}
                                                            />
                                                        </Button>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                        <p className="max-w-[15rem] text-xs text-muted-foreground">
                                            {t('ui.floorplanStencilSizeExplanation')}
                                        </p>
                                    </div>
                                </PopoverContent>
                            </Popover>}
                    </div>
                </div>
                <div ref={divRefCallback}>
                    <div
                        ref={containerRefCallback}
                        // Die Höhe folgt dem Plan, sobald seine Maße bekannt sind — begrenzt nach
                        // oben, damit ein sehr hoher Plan die Liste darunter nicht aus dem Bild
                        // schiebt, und nach unten, damit ein sehr breiter noch bedienbar bleibt.
                        style={planAspect
                            ? { height: Math.round(Math.min(Math.max(canvasSize.width * planAspect, 320), typeof window !== 'undefined' ? window.innerHeight * 0.78 : 720)) }
                            : undefined}
                        className="relative h-[72vh] min-h-[320px] w-full overflow-hidden border-t border-border">
                        {/* Canvas is sized to the MEASURED container, not to the image.
                            paper.js fits the image via zoom and centres it in the view;
                            sizing the canvas to a large image drew the floorplan
                            off-screen (centred at ~half the image size), and a hardcoded
                            800x600 made the fit calculation wrong on every other screen.
                            touch-action:none hands two-finger gestures to the pinch
                            handler instead of to the browser's page zoom. */}
                        <canvas id="editorCanvas" ref={canvasRefCallback} onWheel={panZoom}
                            width={canvasSize.width} height={canvasSize.height}
                            style={{ display: 'block', touchAction: 'none', width: '100%', height: '100%' }}></canvas>
                        {/*
                          * Der Papierkorb des ausgewählten Raums, an seiner oberen rechten Ecke.
                          *
                          * Er wird an der Ecke aufgehängt und nach links oben verschoben, sodass er
                          * neben dem Raum steht und nicht auf ihm — dort verdeckte er die Ecke, die
                          * man gerade fassen will. In die Zeichenfläche eingeschlossen bleibt er
                          * auch dann sichtbar, wenn der Raum am Rand liegt.
                          */}
                        {deleteAnchor && vertexEditIndex >= 0 && spaces?.[vertexEditIndex] &&
                            <Button
                                type="button"
                                variant="destructive"
                                size="icon"
                                aria-label={t('ui.floorplanDeleteRoom', { name: spaces[vertexEditIndex].name || '' })}
                                title={t('ui.floorplanDeleteRoom', { name: spaces[vertexEditIndex].name || '' })}
                                className="absolute h-8 w-8 shadow-md"
                                style={{
                                    left: Math.min(Math.max(deleteAnchor.x + 8, 4), Math.max(canvasSize.width - 36, 4)),
                                    top: Math.min(Math.max(deleteAnchor.y - 40, 4), Math.max(canvasSize.height - 36, 4)),
                                }}
                                onClick={() => properties.spaceDeleteRequested?.(spaces[vertexEditIndex])}
                            >
                                <Trash2 aria-hidden="true" className="h-4 w-4" />
                            </Button>}
                        {/* Bis das Planbild da ist, war die Fläche unter der Werkzeugleiste
                            einfach weiß — sie sah aus wie ein Grundriss ohne Inhalt. */}
                        {!floorplanImageLoaded || floorplanImageFailed
                            ? <div
                                role="status"
                                className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                                {floorplanImageFailed
                                    ? t('floorProperties.planLoadFailed')
                                    : <><Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />{t('floorProperties.planLoading')}</>}
                            </div>
                            : null}
                    </div>
                </div>
                <img
                    key={floor.floorplanImageURI}
                    style={{ display: 'none' }} id="floorplan" src={userAPI.getResourceURL(floor.floorplanImageURI)} width={floor.floorplanImageWidth} height={floor.floorplanImageHeight} alt=""
                    onLoad={() => {
                        // Small delay so the browser has the image fully decoded
                        // before paper.Raster reads from the DOM element.
                        setTimeout(() => {
                            setFloorplaImageLoaded(true);
                        }, 100);
                    }}
                    onError={(e) => {
                        setFloorplanImageFailed(true);
                        console.error('Failed to load floorplan image:', e);
                        console.error('Image URL:', userAPI.getResourceURL(floor.floorplanImageURI));
                    }}></img>
            </div>
            :
            <div className="mt-6 flex flex-col">
                <ImageUploading
                    value={images}
                    onChange={onChange}
                    maxNumber={1}
                    maxFileSize={8000000}
                    dataURLKey="data_url"
                >
                    {({
                        imageList,
                        onImageUpload,
                        onImageRemoveAll,
                        onImageUpdate: _onImageUpdate,
                        onImageRemove: _onImageRemove,
                        isDragging,
                        dragProps,
                    }) => (
                        <div className="">
                            <div className={isDragging ? "border border-destructive" : undefined}
                                {...dragProps}>
                                {imageList.length === 0
                                    ?
                                    (
                                        properties.floor.floorplanImageURI
                                            ?
                                            <img className="floorplanImage" src={userAPI.getResourceURL(properties.floor.floorplanImageURI)} alt="Logo" />
                                            :
                                            <div className="flex h-[200px] w-[300px] flex-col items-center justify-center bg-muted text-sm">Grundriss hierhin ziehen.</div>
                                    )
                                    :
                                    <img className="floorplanImage" src={imageList[0]['data_url']} alt="" />
                                }
                            </div>
                            <div className="mt-6 flex gap-4">
                                <Button type="button" onClick={onImageUpload}>
                                    {t('settingsPanel.companyPanel.uploadFromFile')}
                                </Button>
                                <Button type="button" onClick={onImageRemoveAll}>{t('global.clear')}</Button>
                            </div>
                        </div>
                    )}
                </ImageUploading>
            </div>
        }
        <Dialog open={scalingDialogOpen} onOpenChange={(open) => { if (!open) setScalingDialogOpen(false); }}>
            <DialogContent className="sm:max-w-[400px]">
                <DialogHeader>
                    <DialogTitle>Skalierung Grundriss</DialogTitle>
                    <DialogDescription>Im Bild abgetragene Strecke von {scalingMeasurement.toFixed(0)} Pixeln in Metern.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-2">
                    <Label htmlFor="floorplan-scaling-distance">Strecke</Label>
                    <div className="flex items-center gap-2">
                        <Input id="floorplan-scaling-distance" ref={distanceInputRef} value={scalingDistance}
                            onChange={(event) => setScalingDistance(event.target.value)}
                            aria-invalid={isNaN(scalingDistance) || undefined} />
                        <span className="text-sm text-muted-foreground">m</span>
                    </div>
                    {isNaN(scalingDistance) ? <p className="text-sm text-destructive">Zahl eingeben</p> : null}
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setScalingDialogOpen(false)}>{t('global.cancel')}</Button>
                    <Button type="button" onClick={async () => {
                        const newFloor = { ...properties.floor, floorplanImageScale: scalingMeasurement / scalingDistance };

                        setFloor(newFloor);

                        await updateFloor(newFloor);

                        setScalingDialogOpen(false);
                    }}>{t('global.save')}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog >
    </div >;
}